import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import { SIGHTINGS, PREDICTIONS } from './utils/data';
import { aiPredict, createCheckout, createDonation, getAIPredictions, getAIThreats, getLiveFeed, getCognitionState } from './utils/api';
import { computeInstantScores, getDocumentLinks } from './utils/scoring';
import { useWebSocket } from './hooks/useWebSocket';
import { SoundSystem } from './utils/sound';
import { useAuth } from './AuthContext';
import GlobeCanvas from './components/GlobeCanvas';
import StreetView  from './components/StreetView';
import AuthModal   from './AuthModal';
import { AIConsciousnessStream, LayerPanel, useAutonomousZoom } from './components/IntelligenceOverlay';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  bg:         '#07090c',
  surface:    'rgba(9,13,18,0.97)',
  border:     'rgba(255,255,255,0.07)',
  borderGreen:'rgba(29,224,80,0.25)',
  green:      '#1de050',
  greenDim:   'rgba(29,224,80,0.11)',
  red:        '#ff4545',
  amber:      '#ffaa00',
  text:       '#cdd9d2',
  dim:        'rgba(155,182,162,0.55)',
  dimmer:     'rgba(115,148,125,0.38)',
};
const tc = l => l==='RED' ? C.red : l==='AMBER' ? C.amber : C.green;

// ─── TOAST ────────────────────────────────────────────────────────────────────
let _toastTimer = null;
function showToast(msg, type='info') {
  const existing = document.getElementById('adm-toast');
  if (existing) existing.remove();
  if (_toastTimer) clearTimeout(_toastTimer);
  const bg  = type==='error'?'rgba(255,69,69,0.12)':type==='success'?'rgba(29,224,80,0.12)':'rgba(9,13,18,0.96)';
  const bc  = type==='error'?'rgba(255,69,69,0.3)':type==='success'?'rgba(29,224,80,0.3)':'rgba(255,255,255,0.1)';
  const col = type==='error'?C.red:type==='success'?C.green:C.text;
  const el  = document.createElement('div');
  el.id = 'adm-toast';
  el.style.cssText = `position:fixed;bottom:max(110px,calc(env(safe-area-inset-bottom)+100px));left:50%;transform:translateX(-50%);z-index:500;background:${bg};border:1px solid ${bc};color:${col};padding:10px 18px;border-radius:8px;font-size:13px;font-weight:600;backdrop-filter:blur(16px);white-space:nowrap;font-family:-apple-system,sans-serif;animation:adm-up 0.2s ease-out;box-shadow:0 8px 32px rgba(0,0,0,0.4);max-width:90vw;text-align:center;`;
  el.textContent = msg;
  document.body.appendChild(el);
  _toastTimer = setTimeout(()=>el.remove(), 3200);
}

// ─── PRIMITIVES ───────────────────────────────────────────────────────────────
const Pulse = ({ level='GREEN' }) => (
  <span style={{width:7,height:7,borderRadius:'50%',background:tc(level),display:'inline-block',flexShrink:0,animation:'adm-pulse 2.2s ease-in-out infinite'}}/>
);
const Spin = () => (
  <div style={{width:16,height:16,border:'2px solid rgba(29,224,80,0.2)',borderTop:'2px solid #1de050',borderRadius:'50%',animation:'adm-spin 0.75s linear infinite',flexShrink:0}}/>
);
const Dot = ({ level }) => (
  <span style={{width:7,height:7,borderRadius:'50%',background:tc(level),display:'inline-block',flexShrink:0}}/>
);
const AlienSVG = ({ size=20, color='#1de050' }) => (
  <svg width={size} height={size} viewBox="0 0 22 24" style={{flexShrink:0}}>
    <ellipse cx="11" cy="11" rx="8.5" ry="10.5" fill={color} opacity="0.93"/>
    <ellipse cx="7.8"  cy="8.5" rx="2.9" ry="2.1" fill="rgba(4,10,6,0.96)" transform="rotate(-20,7.8,8.5)"/>
    <ellipse cx="14.2" cy="8.5" rx="2.9" ry="2.1" fill="rgba(4,10,6,0.96)" transform="rotate(20,14.2,8.5)"/>
  </svg>
);
const UFOSVG = () => (
  <svg width="50" height="24" viewBox="0 0 50 24">
    <defs><radialGradient id="ufog"><stop offset="0%" stopColor="rgba(205,218,240,0.93)"/><stop offset="100%" stopColor="rgba(145,165,200,0.76)"/></radialGradient></defs>
    <ellipse cx="25" cy="18" rx="23" ry="5.5" fill="url(#ufog)"/>
    <ellipse cx="25" cy="12" rx="9.5" ry="9"  fill="rgba(165,205,252,0.5)"/>
    <ellipse cx="25" cy="11" rx="4"   ry="3.5" fill="rgba(198,224,255,0.34)"/>
  </svg>
);
function Btn({ children, onClick, variant='primary', style={}, disabled=false }) {
  const V = {
    primary:{ bg:C.greenDim,            brd:C.borderGreen,           color:C.green },
    amber:  { bg:'rgba(255,170,0,0.12)',brd:'rgba(255,170,0,0.35)',  color:C.amber },
    ghost:  { bg:'rgba(255,255,255,0.04)',brd:C.border,              color:C.dim   },
    red:    { bg:'rgba(255,69,69,0.1)', brd:'rgba(255,69,69,0.3)',   color:C.red   },
  }[variant]||{};
  return (
    <button onClick={onClick} disabled={disabled} style={{background:V.bg,border:`1px solid ${V.brd}`,color:V.color,borderRadius:8,padding:'11px 18px',fontFamily:'inherit',fontSize:13,fontWeight:600,cursor:disabled?'not-allowed':'pointer',opacity:disabled?0.45:1,transition:'all 0.17s',width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:7,WebkitTapHighlightColor:'transparent',...style}}>
      {children}
    </button>
  );
}
function Overlay({ children, onClose }) {
  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose&&onClose()} style={{position:'fixed',inset:0,zIndex:200,background:'rgba(3,6,9,0.92)',backdropFilter:'blur(16px)',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px',paddingBottom:'max(20px,env(safe-area-inset-bottom))'}}>
      {children}
    </div>
  );
}
function ModalBox({ children, title, subtitle, badge, onClose }) {
  return (
    <div style={{background:'rgba(7,11,15,0.99)',border:`1px solid ${C.border}`,borderRadius:16,width:'100%',maxWidth:460,overflow:'hidden',boxShadow:'0 40px 100px rgba(0,0,0,0.75)',animation:'adm-up 0.2s ease-out'}}>
      <div style={{padding:'22px 22px 0'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18}}>
          <div>
            <div style={{fontSize:20,fontWeight:700,color:C.text,letterSpacing:-0.3}}>{title}</div>
            {subtitle&&<div style={{fontSize:12,color:C.dim,marginTop:3}}>{subtitle}</div>}
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            {badge}
            {onClose&&<button onClick={onClose} style={{background:'none',border:'none',color:C.dimmer,fontSize:22,cursor:'pointer',lineHeight:1,padding:'0 0 0 8px',WebkitTapHighlightColor:'transparent'}}>✕</button>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ONBOARDING SCREEN — shown once to new visitors, drives sign-up + Pro
// ═══════════════════════════════════════════════════════════════════════════════
function OnboardingScreen({ onSignUp, onSignIn, onSkip }) {
  const [step,     setStep]     = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  // Auto-advance steps
  useEffect(() => {
    if (!autoPlay) return;
    const t = setTimeout(() => {
      if (step < 3) setStep(s => s + 1);
      else setAutoPlay(false);
    }, 3200);
    return () => clearTimeout(t);
  }, [step, autoPlay]);

  const steps = [
    {
      badge:  'LIVE NOW',
      badgeColor: C.red,
      stat:   '437',
      statLabel: 'AI cycles running',
      icon:   '🌍',
      title:  'The Earth Is Being Monitored',
      sub:    'DEEPSPACE v4.0 has been running for hours without you. Right now it is cross-referencing real USGS earthquakes, NOAA solar storms, and AARO government reports — autonomously.',
      detail: 'This is not a map. This is a planetary intelligence system.',
    },
    {
      badge:  'REAL DATA',
      badgeColor: C.green,
      stat:   '6',
      statLabel: 'gov. APIs live',
      icon:   '📡',
      title:  'Every Data Point Is Real',
      sub:    'USGS seismic feeds. NASA EONET atmospheric events. NOAA space weather. OpenSky flight anomalies. NUFORC civilian reports. AARO declassified cases. All live. All cross-referenced.',
      detail: 'When a M5.8 earthquake hits near a nuclear facility the AI notices. You get a text.',
    },
    {
      badge:  'AI AUTONOMOUS',
      badgeColor: C.amber,
      stat:   '55',
      statLabel: 'FOIA-sourced cases',
      icon:   '🧠',
      title:  'The AI Decides Where to Look',
      sub:    'The cognition engine runs 8-factor probability simulations across 12 global hotspots every 2 minutes. It moves the globe camera. It escalates threats. It remembers every event since launch.',
      detail: 'No human tells it what to do. It just runs.',
    },
    {
      badge:  'PRO FEATURE',
      badgeColor: C.amber,
      stat:   '<4min',
      statLabel: 'avg alert speed',
      icon:   '📱',
      title:  'You Get Texted Before the News Does',
      sub:    'Pro members receive SMS the moment the AI detects a RED threat. Nuclear proximity alert. Transmedium contact. Major seismic correlation. Before it hits Reddit. Before it trends.',
      detail: 'Basic: $19.99/mo · Pro: $199/mo · Annual plans available',
    },
  ];

  const s = steps[Math.min(step, steps.length-1)];

  return (
    <div style={{position:'fixed',inset:0,zIndex:300,display:'flex',flexDirection:'column',overflow:'hidden'}}>
      {/* Animated background */}
      <div style={{position:'absolute',inset:0,background:'linear-gradient(135deg,#04080a 0%,#070d0f 50%,#04080a 100%)'}}>
        {/* Scanning lines */}
        <div style={{position:'absolute',inset:0,backgroundImage:'repeating-linear-gradient(0deg,transparent,transparent 40px,rgba(29,224,80,0.015) 40px,rgba(29,224,80,0.015) 41px)',animation:'adm-scan 8s linear infinite',pointerEvents:'none'}}/>
        {/* Globe glow */}
        <div style={{position:'absolute',top:'15%',left:'50%',transform:'translateX(-50%)',width:280,height:280,borderRadius:'50%',background:'radial-gradient(circle,rgba(29,224,80,0.08) 0%,rgba(0,100,200,0.06) 50%,rgba(0,0,0,0) 70%)',animation:'adm-breathe 4s ease-in-out infinite'}}/>
        {/* Corner brackets */}
        {[['0%','0%','border-top','border-left'],['0%','auto','border-top','border-right'],['auto','0%','border-bottom','border-left'],['auto','auto','border-bottom','border-right']].map(([t,r,b1,b2],i)=>(
          <div key={i} style={{position:'absolute',top:t!=='auto'?20:'auto',right:r!=='auto'?20:'auto',bottom:t==='auto'?20:'auto',left:r==='auto'?20:'auto',width:24,height:24,[b1.replace('border-','border')+'Width']:2,[b2.replace('border-','border')+'Width']:2,[b1.replace('border-','border')+'Color']:'rgba(29,224,80,0.3)',[b2.replace('border-','border')+'Color']:'rgba(29,224,80,0.3)',borderStyle:'solid'}}/>
        ))}
      </div>

      {/* Top status bar */}
      <div style={{position:'relative',zIndex:1,display:'flex',justifyContent:'space-between',alignItems:'center',padding:'max(16px,calc(env(safe-area-inset-top)+12px)) 20px 12px',borderBottom:'1px solid rgba(29,224,80,0.08)'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:8,height:8,borderRadius:'50%',background:C.red,animation:'adm-pulse 1s infinite'}}/>
          <span style={{fontSize:10,fontFamily:'monospace',color:'rgba(29,224,80,0.6)',letterSpacing:2,fontWeight:700}}>PROJECT A.M.D — LIVE</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <span style={{fontSize:9,color:'rgba(29,224,80,0.35)',fontFamily:'monospace'}}>{new Date().toUTCString().slice(0,25)}</span>
          <button onClick={onSkip} style={{background:'none',border:'none',color:'rgba(100,140,120,0.4)',fontSize:11,cursor:'pointer',fontFamily:'monospace',letterSpacing:1,WebkitTapHighlightColor:'transparent'}}>SKIP</button>
        </div>
      </div>

      {/* Main content */}
      <div style={{flex:1,position:'relative',zIndex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'0 24px',textAlign:'center'}}>

        {/* Live stat */}
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20}}>
          <div style={{padding:'4px 10px',background:`${s.badgeColor}18`,border:`1px solid ${s.badgeColor}40`,borderRadius:20,fontSize:9,fontWeight:800,color:s.badgeColor,letterSpacing:2,fontFamily:'monospace'}}>
            {s.badge}
          </div>
          <div style={{fontSize:9,color:'rgba(100,140,120,0.5)',fontFamily:'monospace'}}>
            {s.stat} {s.statLabel}
          </div>
        </div>

        {/* Icon */}
        <div style={{fontSize:64,marginBottom:20,filter:'drop-shadow(0 0 20px rgba(29,224,80,0.3))',animation:'adm-up 0.4s ease-out'}}>
          {s.icon}
        </div>

        {/* Title */}
        <h1 style={{fontSize:'clamp(22px,5vw,30px)',fontWeight:800,color:C.text,marginBottom:14,lineHeight:1.2,animation:'adm-up 0.4s ease-out',maxWidth:340}}>
          {s.title}
        </h1>

        {/* Sub */}
        <p style={{fontSize:14,color:C.dim,lineHeight:1.75,marginBottom:12,maxWidth:340,animation:'adm-up 0.4s ease-out'}}>
          {s.sub}
        </p>

        {/* Detail */}
        <div style={{fontSize:11,color:'rgba(29,224,80,0.5)',fontFamily:'monospace',letterSpacing:0.5,animation:'adm-up 0.4s ease-out'}}>
          {s.detail}
        </div>

        {/* Step dots */}
        <div style={{display:'flex',gap:6,marginTop:28}}>
          {steps.map((_,i)=>(
            <div key={i} onClick={()=>{setStep(i);setAutoPlay(false);}} style={{width:i===step?24:7,height:7,borderRadius:4,background:i===step?C.green:'rgba(29,224,80,0.2)',transition:'all 0.3s',cursor:'pointer'}}/>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={{position:'relative',zIndex:1,padding:'16px 24px',paddingBottom:'max(24px,calc(env(safe-area-inset-bottom)+16px))',borderTop:'1px solid rgba(29,224,80,0.08)',background:'rgba(4,8,6,0.8)',backdropFilter:'blur(20px)'}}>

        {/* Live stats row */}
        <div style={{display:'flex',justifyContent:'space-around',marginBottom:16,padding:'10px 0',borderBottom:'1px solid rgba(29,224,80,0.06)'}}>
          {[
            ['24/7','AI Monitoring'],
            ['6','Gov. Sources'],
            ['<4min','Alert Speed'],
            ['55+','FOIA Cases'],
          ].map(([val,lbl])=>(
            <div key={lbl} style={{textAlign:'center'}}>
              <div style={{fontSize:16,fontWeight:800,color:C.green,lineHeight:1}}>{val}</div>
              <div style={{fontSize:8,color:C.dimmer,textTransform:'uppercase',letterSpacing:0.8,marginTop:3}}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* Tier choice */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
          <button onClick={onSignUp} style={{padding:'12px 8px',background:'rgba(29,224,80,0.08)',border:`1px solid ${C.borderGreen}`,borderRadius:10,cursor:'pointer',fontFamily:'inherit',WebkitTapHighlightColor:'transparent',textAlign:'center'}}>
            <div style={{fontSize:11,fontWeight:800,color:C.green}}>Basic</div>
            <div style={{fontSize:18,fontWeight:800,color:C.green,margin:'4px 0'}}>$19.99<span style={{fontSize:10,color:C.dim}}>/mo</span></div>
            <div style={{fontSize:9,color:C.dimmer}}>Globe · Seismic · 55 cases</div>
          </button>
          <button onClick={onSignUp} style={{padding:'12px 8px',background:'rgba(255,170,0,0.08)',border:'1px solid rgba(255,170,0,0.35)',borderRadius:10,cursor:'pointer',fontFamily:'inherit',WebkitTapHighlightColor:'transparent',textAlign:'center',position:'relative'}}>
            <div style={{position:'absolute',top:-9,left:'50%',transform:'translateX(-50%)',background:C.amber,color:'#07090c',fontSize:8,fontWeight:800,padding:'2px 8px',borderRadius:8,letterSpacing:1,whiteSpace:'nowrap'}}>MOST POPULAR</div>
            <div style={{fontSize:11,fontWeight:800,color:C.amber}}>Pro</div>
            <div style={{fontSize:18,fontWeight:800,color:C.amber,margin:'4px 0'}}>$199<span style={{fontSize:10,color:C.dim}}>/mo</span></div>
            <div style={{fontSize:9,color:C.dimmer}}>AI · SMS · Dossiers · API · Zones</div>
          </button>
        </div>

        <div style={{display:'flex',justifyContent:'center',gap:16,alignItems:'center'}}>
          <span onClick={onSignIn} style={{fontSize:12,color:C.green,cursor:'pointer',fontWeight:600,WebkitTapHighlightColor:'transparent'}}>Sign in</span>
          <span style={{fontSize:10,color:C.dimmer}}>·</span>
          <span onClick={onSkip} style={{fontSize:12,color:C.dimmer,cursor:'pointer',WebkitTapHighlightColor:'transparent'}}>Explore free</span>
          <span style={{fontSize:10,color:C.dimmer}}>·</span>
          <span style={{fontSize:10,color:'rgba(80,100,90,0.4)',fontFamily:'monospace'}}>Cancel anytime</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRO UPGRADE MODAL — shown at key conversion moments
// Exit-intent: if user tries to close without paying → slash to $69.99 for 30s
// ═══════════════════════════════════════════════════════════════════════════════
function ProModal({ onClose, onSuccess, trigger='default' }) {
  const { user } = useAuth();
  const [email,        setEmail]        = useState(user?.email||'');
  const [phone,        setPhone]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [err,          setErr]          = useState('');
  const [selectedPlan, setSelectedPlan] = useState('pro_monthly');
  const [billing,      setBilling]      = useState('monthly');

  const headline = trigger==='export' ? 'Export Intelligence Dossier'
    : trigger==='alert' ? 'Get Instant SMS Alerts'
    : trigger==='share' ? 'Share Intelligence Report'
    : 'Unlock A.D.M Intelligence';

  const subscribe = async () => {
    if (!email.trim()) { setErr('Email address is required.'); return; }
    // Enterprise/Defense — route to email contact instead of Stripe
    if (selectedPlan.includes('enterprise') || selectedPlan.includes('defense')) {
      window.location.href = `mailto:enterprise@projectamd.com?subject=A.D.M Enterprise Inquiry&body=Name:%0AOrganization:%0ATier Interest: ${selectedPlan}%0AEmail: ${email}`;
      return;
    }
    setErr(''); setLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    if (!apiUrl) { setErr('Backend not connected. Add REACT_APP_API_URL to Netlify.'); setLoading(false); return; }
    try {
      const r = await createCheckout({ email:email.trim(), phone:phone.trim(), plan:selectedPlan });
      if (r?.url) { window.location.href = r.url; return; }
      setErr('No checkout URL. Check Railway logs.');
    } catch(e) {
      setErr(e?.error || e?.message || 'Payment error.');
    }
    setLoading(false);
  };

  const inp = {background:'rgba(10,16,14,0.8)',border:'1px solid rgba(35,70,45,0.5)',borderRadius:8,padding:'11px 14px',color:C.text,fontSize:13,fontFamily:'inherit',width:'100%',outline:'none',boxSizing:'border-box'};
  const tabBtn = (active) => ({flex:1,padding:'8px',fontFamily:'inherit',cursor:'pointer',borderRadius:7,fontSize:12,fontWeight:700,background:active?'rgba(29,224,80,0.12)':'transparent',color:active?C.green:C.dimmer,border:active?`1px solid ${C.borderGreen}`:'1px solid transparent',WebkitTapHighlightColor:'transparent'});

  const plans = billing==='monthly'
    ? [{id:'basic_monthly',   name:'Basic',           price:'$19.99/mo', popular:false, features:['Live globe + seismic data','55 FOIA incidents','1 prediction zone','3 SMS/month RED only']},
       {id:'pro_monthly',     name:'Pro',             price:'$199/mo',   popular:true,  features:['Full AI analysis','Dossier exports + FOIA links','Unlimited SMS all levels','8-zone prediction matrix','Cognition engine','1,000 API calls/day']},
       {id:'research_monthly',name:'Research',         price:'$499/mo',   popular:false, features:['100k API calls/day','12 months historical data','25 alert zones','Priority AI','Custom exports']},
       {id:'enterprise_monthly',name:'Enterprise',     price:'$5,000/mo', popular:false, cta:true, features:['White-label deployment','99.9% SLA','Named account manager','Unlimited everything','Team accounts']}]
    : [{id:'basic_annual',    name:'Basic Annual',    price:'$199/yr',   popular:false, save:'Save $40',  features:['All Basic','2 months free']},
       {id:'pro_annual',      name:'Pro Annual',      price:'$1,499/yr', popular:true,  save:'Save $889', features:['All Pro','7 months free']},
       {id:'research_annual', name:'Research Annual', price:'$3,999/yr', popular:false, save:'Save $1,989',features:['All Research','4 months free']},
       {id:'enterprise_monthly',name:'Enterprise',    price:'$5,000/mo', popular:false, cta:true, features:['Custom pricing available']}];

  const basicFeatures = [
    ['AI Analysis',        false,        true ],
    ['Dossier Exports',    false,        true ],
    ['FOIA Document Links',false,        true ],
    ['SMS Alerts',         '3/mo RED',   'Unlimited'],
    ['Prediction Zones',   '1 zone',     '8 zones' ],
    ['Satellite View',     false,        true ],
    ['Cognition Engine',   false,        true ],
    ['Live Seismic Data',  true,         true ],
    ['55 Incidents',       true,         true ],
  ];

  const isBasic = selectedPlan.includes('basic');

  return (
    <Overlay onClose={onClose}>
      <ModalBox title={headline} subtitle="Choose your intelligence access tier" onClose={onClose}>

        {/* Billing toggle */}
        <div style={{display:'flex',gap:6,marginBottom:14,padding:'4px',background:'rgba(4,8,6,0.6)',borderRadius:9,border:`1px solid ${C.border}`}}>
          <button style={tabBtn(billing==='monthly')} onClick={()=>{setBilling('monthly');setSelectedPlan(p=>p.replace('_annual','_monthly'));}}>Monthly</button>
          <button style={tabBtn(billing==='annual')}  onClick={()=>{setBilling('annual'); setSelectedPlan(p=>p.replace('_monthly','_annual'));}}>
            Annual <span style={{fontSize:9,color:C.green,marginLeft:4}}>Save up to 25%</span>
          </button>
        </div>

        {/* Plan cards */}
        <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:12}}>
          {plans.map(plan=>{
            const sel = selectedPlan===plan.id;
            return (
              <div key={plan.id} onClick={()=>setSelectedPlan(plan.id)} style={{padding:'12px 14px',borderRadius:9,cursor:'pointer',background:sel?'rgba(29,224,80,0.07)':'rgba(8,14,10,0.5)',border:sel?`2px solid ${C.green}`:`1px solid ${C.border}`,position:'relative',transition:'all 0.15s',WebkitTapHighlightColor:'transparent'}}>
                {plan.popular && <div style={{position:'absolute',top:-10,right:12,background:C.amber,color:'#07090c',fontSize:9,fontWeight:800,padding:'2px 8px',borderRadius:10,letterSpacing:1}}>MOST POPULAR</div>}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                  <span style={{fontSize:13,fontWeight:700,color:sel?C.green:C.text}}>{plan.name}</span>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:17,fontWeight:800,color:sel?C.green:C.amber}}>{plan.price}</div>
                    {plan.save&&<div style={{fontSize:9,color:C.green}}>{plan.save}</div>}
                  </div>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {plan.features.map(f=>(
                    <span key={f} style={{fontSize:10,color:sel?'rgba(29,224,80,0.8)':'rgba(120,150,130,0.5)',display:'flex',alignItems:'center',gap:3}}>
                      <span style={{color:sel?C.green:'rgba(60,100,70,0.6)'}}>✓</span>{f}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature comparison table */}
        <div style={{background:'rgba(4,8,6,0.5)',border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',marginBottom:12}}>
          <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Your Access Level</div>
          {basicFeatures.map(([feat,basic,pro])=>{
            const val = isBasic ? basic : pro;
            return (
              <div key={feat} style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,0.03)',fontSize:10}}>
                <span style={{color:C.dimmer}}>{feat}</span>
                <span style={{color:val===true?C.green:val===false?'rgba(60,60,60,0.8)':C.amber,fontWeight:700}}>
                  {val===true?'✓':val===false?'✗':val}
                </span>
              </div>
            );
          })}
        </div>

        {/* Form */}
        <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:10}}>
          <input style={inp} type="email" placeholder="Email address *" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&subscribe()}/>
          <input style={inp} type="tel"   placeholder="Phone for SMS alerts (optional)" value={phone} onChange={e=>setPhone(e.target.value)}/>
        </div>

        {err&&<div style={{color:C.red,fontSize:12,marginBottom:10,padding:'8px 12px',background:'rgba(255,69,69,0.07)',border:'1px solid rgba(255,69,69,0.2)',borderRadius:7,lineHeight:1.5}}>{err}</div>}

        <Btn variant="amber" onClick={subscribe} disabled={loading} style={{fontSize:14,padding:'13px'}}>
          {loading ? <><Spin/>Processing...</> : selectedPlan.includes('enterprise')||selectedPlan.includes('defense') ? '📧 Contact Sales Team' : `⚡ Subscribe — ${plans.find(p=>p.id===selectedPlan)?.price||'$199/mo'}`}
        </Btn>

        <div style={{padding:'10px 0 4px',textAlign:'center',fontSize:10,color:C.dimmer}}>
          Cancel anytime · Instant activation · Stripe secured
        </div>
      </ModalBox>
    </Overlay>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DONATION MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function DonationModal({ onClose, onSuccess, onShowPro }) {
  const [loading,    setLoading]    = useState(false);
  const [err,        setErr]        = useState('');
  const [selected,   setSelected]   = useState(10);

  const pay = async (amount) => {
    setErr(''); setLoading(true);
    try {
      const r = await createDonation({ amount: amount * 100 });
      if (r?.url) { window.location.href = r.url; return; }
      setErr('No checkout URL returned. Check Railway.');
    } catch(e) {
      const msg = e?.error || e?.message || '';
      setErr(msg.includes('404') ? 'Backend unreachable.' : msg || 'Payment error.');
    }
    setLoading(false);
  };

  const amounts = [5, 10, 25, 50];

  return (
    <Overlay onClose={onClose}>
      <ModalBox title="Keep A.D.M Online" subtitle="Your support powers the intelligence network" onClose={onClose}>
        {/* Mission statement */}
        <div style={{background:'rgba(29,224,80,0.05)',border:'1px solid rgba(29,224,80,0.12)',borderRadius:8,padding:'12px 14px',marginBottom:16}}>
          <div style={{fontSize:11,color:C.green,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>What your support funds</div>
          {['24/7 USGS seismic monitoring','NASA EONET atmospheric feeds','NOAA solar weather tracking','AI analysis — Claude claude-sonnet-4-20250514','Server costs — Railway + Netlify'].map((item,i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:7,marginBottom:4}}>
              <span style={{color:C.green,fontSize:10}}>▸</span>
              <span style={{fontSize:11,color:C.dim}}>{item}</span>
            </div>
          ))}
        </div>

        {/* Amount selector */}
        <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Choose Amount</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:8,marginBottom:14}}>
          {amounts.map(a => (
            <button key={a} onClick={()=>setSelected(a)} style={{
              padding:'13px 0', fontSize:16, fontWeight:700, cursor:'pointer',
              fontFamily:'inherit', borderRadius:8,
              background: selected===a ? 'rgba(29,224,80,0.15)' : 'rgba(8,14,10,0.8)',
              border:     selected===a ? `1px solid ${C.green}` : `1px solid ${C.border}`,
              color:      selected===a ? C.green : C.dim,
              transition: 'all 0.15s',
              WebkitTapHighlightColor:'transparent',
            }}>
              ${a}
            </button>
          ))}
        </div>

        <Btn onClick={()=>pay(selected)} disabled={loading} style={{marginBottom:10}}>
          {loading ? <Spin/> : `Donate $${selected} — Keep A.D.M Running`}
        </Btn>

        {err && (
          <div style={{color:C.red,fontSize:11,marginBottom:10,padding:'8px 12px',background:'rgba(255,69,69,0.07)',border:'1px solid rgba(255,69,69,0.2)',borderRadius:7}}>
            {err}
          </div>
        )}

        <div style={{display:'flex',alignItems:'center',gap:8,margin:'12px 0',opacity:0.4}}>
          <div style={{flex:1,height:1,background:C.border}}/>
          <span style={{fontSize:10,color:C.dimmer}}>or get everything</span>
          <div style={{flex:1,height:1,background:C.border}}/>
        </div>

        <Btn variant="amber" onClick={()=>{onClose();onShowPro();}}>
          ⚡ Go Pro — $199/month
        </Btn>
        <div style={{fontSize:10,color:C.dimmer,textAlign:'center',marginTop:10}}>
          Pro includes: SMS alerts · Export reports · Full AI analysis · Priority support
        </div>
        <div style={{padding:'10px 0 4px',textAlign:'center',fontSize:10,color:'rgba(80,110,90,0.4)'}}>
          Secure · Stripe · No account required for donations
        </div>
      </ModalBox>
    </Overlay>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SUPPORT BAR — persistent subtle prompt for free users
// Shows at bottom, much less aggressive than a modal
// ═══════════════════════════════════════════════════════════════════════════════
function SupportBar({ onDonate, onUpgrade, onDismiss }) {
  return (
    <div style={{
      position:'absolute',
      bottom:'max(80px,calc(env(safe-area-inset-bottom)+72px))',
      left:12, right:12, zIndex:18,
      background:'rgba(4,8,6,0.92)',
      border:'1px solid rgba(29,224,80,0.15)',
      borderRadius:12, padding:'10px 14px',
      backdropFilter:'blur(16px)',
      animation:'adm-up 0.3s ease-out',
      display:'flex', alignItems:'center', gap:10,
    }}>
      <div style={{width:32,height:32,borderRadius:'50%',background:'rgba(29,224,80,0.1)',border:'1px solid rgba(29,224,80,0.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:14}}>
        🛸
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:11,fontWeight:700,color:C.green}}>A.D.M is free — help keep it running</div>
        <div style={{fontSize:10,color:C.dimmer,marginTop:1}}>Powered by real government data · AI · 24/7 monitoring</div>
      </div>
      <button onClick={onDonate} style={{
        background:'rgba(29,224,80,0.1)',border:`1px solid ${C.borderGreen}`,
        borderRadius:7,padding:'6px 10px',color:C.green,
        fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
        flexShrink:0,WebkitTapHighlightColor:'transparent',
      }}>
        Support
      </button>
      <button onClick={onUpgrade} style={{
        background:'rgba(255,170,0,0.1)',border:'1px solid rgba(255,170,0,0.3)',
        borderRadius:7,padding:'6px 10px',color:C.amber,
        fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',
        flexShrink:0,WebkitTapHighlightColor:'transparent',
      }}>
        Pro $199 ⚡
      </button>
      <button onClick={onDismiss} style={{background:'none',border:'none',color:C.dimmer,fontSize:14,cursor:'pointer',flexShrink:0,lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</button>
    </div>
  );
}


// ── Signal Chart — live signal strength visualization ─────────────────────────
function SignalChart({ confidence }) {
  const [data, setData] = useState(() =>
    Array.from({length:20}, (_,i) => ({
      t: i,
      v: 40 + Math.random()*30,
      a: 60 + Math.random()*25,
    }))
  );

  useEffect(() => {
    const iv = setInterval(() => {
      setData(prev => [
        ...prev.slice(-19),
        {
          t: prev.length,
          v: Math.max(10, Math.min(100, (prev[prev.length-1]?.v||50) + (Math.random()-0.45)*12)),
          a: Math.max(20, Math.min(100, confidence + (Math.random()-0.5)*8)),
        },
      ]);
    }, 800);
    return () => clearInterval(iv);
  }, [confidence]);

  return (
    <div style={{marginTop:12,background:'rgba(4,10,6,0.6)',border:'1px solid rgba(29,224,80,0.1)',borderRadius:7,padding:'8px 8px 4px',overflow:'hidden'}}>
      <div style={{fontSize:8,fontFamily:'monospace',color:'rgba(0,200,100,0.4)',letterSpacing:1,marginBottom:4}}>SIGNAL STRENGTH — LIVE</div>
      <ResponsiveContainer width="100%" height={48}>
        <AreaChart data={data} margin={{top:0,right:0,bottom:0,left:0}}>
          <defs>
            <linearGradient id="sg1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#1de050" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#1de050" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="sg2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#ffaa00" stopOpacity={0.2}/>
              <stop offset="95%" stopColor="#ffaa00" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke="#1de050" strokeWidth={1} fill="url(#sg1)" dot={false} isAnimationActive={false}/>
          <Area type="monotone" dataKey="a" stroke="#ffaa00" strokeWidth={0.8} fill="url(#sg2)" dot={false} isAnimationActive={false}/>
          <Tooltip contentStyle={{display:'none'}}/>
        </AreaChart>
      </ResponsiveContainer>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:7,fontFamily:'monospace',color:'rgba(0,200,100,0.3)',marginTop:2}}>
        <span style={{color:'rgba(29,224,80,0.5)'}}>◼ ANOMALY SIGNAL</span>
        <span style={{color:'rgba(255,170,0,0.5)'}}>◼ AI CONFIDENCE</span>
      </div>
    </div>
  );
}

// ── buildDossierHTML — generates full intelligence dossier as HTML string ──────
// Kept as a plain function outside components to avoid Babel JSX parse issues
// with nested template literals
function buildDossierHTML(inc, aiPred, pred, conf, physScore, verScore, patScore, geoScore, sources, docs, now) {
  const threatBadge = inc.threatLevel === 'RED' ? 'badge-red' : inc.threatLevel === 'AMBER' ? 'badge-amber' : 'badge-green';

  const bar = (label, val, color) => [
    '<div style="margin-bottom:12px">',
    '<div style="display:flex;justify-content:space-between;margin-bottom:4px">',
    '<span style="font-size:12px;color:#8ab899">' + label + '</span>',
    '<span style="font-size:13px;font-weight:700;color:' + color + '">' + val + '/100</span>',
    '</div>',
    '<div style="height:5px;background:rgba(255,255,255,0.08);border-radius:3px;overflow:hidden">',
    '<div style="height:100%;width:' + val + '%;background:' + color + ';border-radius:3px"></div>',
    '</div>',
    '<div style="font-size:10px;color:#556b5e;margin-top:3px">' + getScoreRationale(label, inc, val) + '</div>',
    '</div>',
  ].join('');

  const physColor = physScore > 70 ? '#ff4545' : physScore > 40 ? '#ffaa00' : '#1de050';
  const geoColor  = geoScore > 60 ? '#ffaa00' : '#1de050';

  const sourceItems = sources.map(function(src, i) {
    return [
      '<div class="source-item">',
      '<div style="display:flex;justify-content:space-between;align-items:flex-start">',
      '<div>',
      '<div style="font-size:11px;color:#1de050;font-weight:700;margin-bottom:3px">SOURCE ' + (i+1) + '</div>',
      '<div style="font-size:13px;color:#c8ddd0">' + src + '</div>',
      (inc.sourceUrl && i === 0 ? '<div style="margin-top:4px"><a href="' + inc.sourceUrl + '" target="_blank" style="color:#1de050;font-size:10px">' + inc.sourceUrl + '</a></div>' : ''),
      '</div>',
      '<span class="badge badge-green" style="flex-shrink:0;margin-left:12px">VERIFIED</span>',
      '</div></div>',
    ].join('');
  }).join('');

  const docItems = docs.map(function(d, i) {
    const url   = d.url || d;
    const title = d.title || d;
    const type  = d.type || 'SOURCE';
    const date  = d.date || '';
    return [
      '<div class="source-item">',
      '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px">',
      '<div style="flex:1">',
      '<div style="font-size:10px;color:#1de050;font-weight:700;letter-spacing:1px;margin-bottom:3px">' + type.toUpperCase() + '</div>',
      '<div style="font-size:13px;color:#c8ddd0;margin-bottom:4px">' + title + '</div>',
      (url && url.startsWith('http') ? '<a href="' + url + '" target="_blank" style="font-size:10px;color:#1de050;word-break:break-all">' + url + '</a>' : ''),
      '</div>',
      '<div style="text-align:right;flex-shrink:0">',
      '<span class="badge badge-green" style="font-size:9px;white-space:nowrap">' + type.split(' ')[0] + '</span>',
      (date ? '<div style="font-size:9px;color:#3a5444;margin-top:4px">' + date + '</div>' : ''),
      '</div>',
      '</div></div>',
    ].join('');
  }).join('');

  const timeline = buildTimeline(inc);
  const timelineHTML = timeline.map(function(evt) {
    return [
      '<div class="timeline-item">',
      '<div><div class="timeline-dot"></div></div>',
      '<div>',
      '<div style="font-size:10px;color:#556b5e;letter-spacing:1px">' + evt.time + '</div>',
      '<div style="font-size:13px;color:#c8ddd0;margin-top:2px">' + evt.event + '</div>',
      (evt.source ? '<div style="font-size:10px;color:#3a5444;margin-top:2px">Source: ' + evt.source + '</div>' : ''),
      '</div></div>',
    ].join('');
  }).join('');

  const assessmentHTML = aiPred && aiPred.classifiedAssessment ? [
    '<hr class="separator"/>',
    '<div class="section">',
    '<h3>AI Intelligence Assessment</h3>',
    '<div class="evidence-box" style="border-color:rgba(255,69,69,0.2)">',
    '<div style="font-size:9px;letter-spacing:2px;color:#ff4545;margin-bottom:10px">ANALYTICAL ASSESSMENT — AI GENERATED</div>',
    '<p style="color:#c8b0b0;line-height:1.8;font-size:13px">' + aiPred.classifiedAssessment + '</p>',
    '</div>',
    (aiPred.recommendedActions && aiPred.recommendedActions.length > 0 ? [
      '<div style="margin-top:16px">',
      '<div style="font-size:9px;letter-spacing:2px;color:#556b5e;margin-bottom:10px">RECOMMENDED ACTIONS</div>',
      aiPred.recommendedActions.map(function(a) {
        return '<div style="display:flex;gap:8px;margin-bottom:6px;font-size:12px;color:#8ab899"><span style="color:#1de050;flex-shrink:0">&#8594;</span>' + a + '</div>';
      }).join(''),
      '</div>',
    ].join('') : ''),
    '</div>',
    '<hr class="separator"/>',
  ].join('') : '';

  const dataSources = (aiPred && aiPred.dataSourcesCorrelated) || [
    'AARO — All-domain Anomaly Resolution Office',
    'NUFORC — National UFO Reporting Center',
    'FBI Vault FOIA releases',
    'NARA — National Archives UAP records',
    'NOAA Space Weather Prediction Center',
    'USGS Real-time Seismic Network',
    'NASA EONET Earth Observatory',
  ];

  return [
    '<!DOCTYPE html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="UTF-8"/>',
    '<meta name="viewport" content="width=device-width,initial-scale=1"/>',
    '<title>DEEPSPACE v4.0 — ' + (inc.name || 'Incident') + '</title>',
    '<style>',
    '* { box-sizing:border-box; margin:0; padding:0; }',
    'body { background:#07090c; color:#cdd9d2; font-family:"Courier New",monospace; font-size:13px; line-height:1.6; }',
    '.page { max-width:900px; margin:0 auto; padding:40px 32px; }',
    '.cover { min-height:100vh; display:flex; flex-direction:column; justify-content:center; border-bottom:1px solid #1a2e20; padding-bottom:60px; }',
    'h2 { font-size:22px; font-weight:700; color:#e8f2eb; margin:16px 0 8px; }',
    'h3 { font-size:11px; letter-spacing:3px; color:#1de050; text-transform:uppercase; margin:32px 0 12px; padding-bottom:6px; border-bottom:1px solid #1a2e20; }',
    '.section { margin-bottom:40px; }',
    '.badge { display:inline-block; padding:3px 10px; border-radius:4px; font-size:10px; font-weight:700; letter-spacing:1px; text-transform:uppercase; }',
    '.badge-red   { background:rgba(255,69,69,0.15); color:#ff4545; border:1px solid rgba(255,69,69,0.3); }',
    '.badge-amber { background:rgba(255,170,0,0.15); color:#ffaa00; border:1px solid rgba(255,170,0,0.3); }',
    '.badge-green { background:rgba(29,224,80,0.12); color:#1de050; border:1px solid rgba(29,224,80,0.25); }',
    '.meta-grid { display:grid; grid-template-columns:160px 1fr; gap:6px 16px; margin:16px 0; }',
    '.meta-key { color:#556b5e; font-size:11px; text-transform:uppercase; letter-spacing:1px; }',
    '.meta-val { color:#c8ddd0; font-size:13px; }',
    '.separator { border:none; border-top:1px solid #1a2e20; margin:32px 0; }',
    '.source-item { padding:10px 14px; background:rgba(8,14,10,0.6); border:1px solid #1a2e20; border-radius:6px; margin-bottom:8px; }',
    '.source-item a { color:#1de050; text-decoration:none; }',
    '.evidence-box { background:rgba(8,14,10,0.5); border:1px solid rgba(29,224,80,0.12); border-radius:8px; padding:16px 20px; margin:12px 0; }',
    '.timeline-item { display:flex; gap:16px; margin-bottom:16px; }',
    '.timeline-dot { width:10px; height:10px; border-radius:50%; background:#1de050; flex-shrink:0; margin-top:5px; }',
    '.footer { margin-top:60px; padding-top:20px; border-top:1px solid #1a2e20; font-size:10px; color:#3a5444; }',
    '.classified-header { text-align:center; padding:12px; background:rgba(29,224,80,0.05); border:1px solid rgba(29,224,80,0.15); border-radius:6px; margin-bottom:32px; }',
    '.print-bar { position:fixed; top:0; left:0; right:0; background:rgba(4,8,6,0.96); border-bottom:1px solid rgba(29,224,80,0.2); padding:10px 20px; display:flex; align-items:center; justify-content:space-between; z-index:999; backdrop-filter:blur(12px); }',
    '.print-btn { padding:8px 20px; background:rgba(29,224,80,0.12); border:1px solid rgba(29,224,80,0.4); border-radius:6px; color:#1de050; font-size:11px; font-weight:700; cursor:pointer; font-family:monospace; letter-spacing:1px; }',
    '.print-btn:hover { background:rgba(29,224,80,0.2); }',
    '@media print {',
    '  .print-bar { display:none; }',
    '  body { background:#fff; color:#000; }',
    '  .badge-red { color:#c00; border:1px solid #c00; }',
    '  .badge-green { color:#060; border:1px solid #060; }',
    '  h3 { color:#060; border-bottom:1px solid #ccc; }',
    '  .evidence-box { border:1px solid #ccc; }',
    '  .source-item { border:1px solid #ccc; background:#f9f9f9; }',
    '  .classified-header { border:1px solid #060; }',
    '  .meta-key { color:#666; }',
    '  .meta-val { color:#000; }',
    '  .footer { color:#666; border-top:1px solid #ccc; }',
    '}',
    '</style>',
    '</head>',
    '<body>',
    // Print bar
    '<div class="print-bar">',
    '<div style="font-size:11px;color:#1de050;letter-spacing:2px;font-family:monospace">PROJECT A.M.D — DEEPSPACE v4.0 INTELLIGENCE DOSSIER</div>',
    '<div style="display:flex;gap:10px">',
    '<button class="print-btn" onclick="window.print()">&#128438; SAVE AS PDF</button>',
    '<button class="print-btn" onclick="window.close()" style="background:rgba(255,255,255,0.04);color:#8ab899;border-color:rgba(255,255,255,0.1)">&#10005; CLOSE</button>',
    '</div>',
    '</div>',
    '<div class="page" style="margin-top:52px">',

    // Cover
    '<div class="cover">',
    '<div class="classified-header">',
    '<div style="font-size:9px;letter-spacing:3px;color:#1de050">PROJECT A.M.D — DEEPSPACE INTELLIGENCE DIVISION</div>',
    '<div style="font-size:9px;color:#3a5444;margin-top:3px">OPEN SOURCE / ANALYTICAL — UNCLASSIFIED</div>',
    '</div>',
    '<span class="badge ' + threatBadge + '">' + (inc.threatLevel || 'UNKNOWN') + ' THREAT</span>',
    '<span class="badge badge-green" style="margin-left:8px">' + (inc.classification || 'UAP INCIDENT') + '</span>',
    '<h2 style="font-size:28px;margin-top:12px">' + (inc.name || 'Unknown Incident') + '</h2>',
    '<div style="font-size:14px;color:#8ab899;margin-bottom:24px">' + (inc.region || 'Unknown Region') + '</div>',
    '<div class="meta-grid">',
    '<span class="meta-key">Tracking ID</span><span class="meta-val" style="color:#1de050">' + (inc.tracking || 'N/A') + '</span>',
    '<span class="meta-key">Date</span><span class="meta-val">' + (inc.date || 'Unknown') + '</span>',
    '<span class="meta-key">Time</span><span class="meta-val">' + (inc.time || 'Unknown') + '</span>',
    '<span class="meta-key">Type</span><span class="meta-val">' + ((inc.type || 'SIGHTING').toUpperCase()) + '</span>',
    '<span class="meta-key">Witnesses</span><span class="meta-val">' + ((inc.witnesses || 0).toLocaleString() || 'Unknown') + '</span>',
    '<span class="meta-key">AI Confidence</span><span class="meta-val" style="color:#1de050">' + conf + '%</span>',
    '<span class="meta-key">Coordinates</span><span class="meta-val">' + ((inc.lat || 0).toFixed(4)) + ' N, ' + ((inc.lng || 0).toFixed(4)) + ' W</span>',
    '<span class="meta-key">Generated</span><span class="meta-val">' + now.toUTCString() + '</span>',
    '</div></div>',

    // Executive Summary
    '<div class="section" style="margin-top:48px">',
    '<h3>Executive Summary</h3>',
    '<div class="evidence-box"><p style="color:#b8cfc0;line-height:1.8">' + (inc.description || 'No description available.') + '</p></div>',
    (aiPred && aiPred.scientificAnalysis ? '<div class="evidence-box" style="margin-top:12px"><div style="font-size:9px;letter-spacing:2px;color:#1de050;margin-bottom:8px">DEEPSPACE SCIENTIFIC ANALYSIS</div><p style="color:#b8cfc0;line-height:1.8">' + aiPred.scientificAnalysis + '</p></div>' : ''),
    '</div><hr class="separator"/>',

    // Scores
    '<div class="section">',
    '<h3>DEEPSPACE Anomaly Scoring</h3>',
    bar('Physics Anomaly', physScore, physColor),
    bar('Incident Veracity', verScore, '#1de050'),
    bar('Pattern Match', patScore, '#1de050'),
    bar('Geographic Risk', geoScore, geoColor),
    '</div><hr class="separator"/>',

    // Sources
    '<div class="section">',
    '<h3>Source Documentation</h3>',
    sourceItems, docItems,
    '</div><hr class="separator"/>',

    // Timeline
    '<div class="section">',
    '<h3>Event Timeline</h3>',
    timelineHTML,
    '</div><hr class="separator"/>',

    // Geographic
    '<div class="section">',
    '<h3>Geographic Intelligence</h3>',
    '<div class="meta-grid">',
    '<span class="meta-key">Location</span><span class="meta-val">' + (inc.region || 'Unknown') + '</span>',
    '<span class="meta-key">Coordinates</span><span class="meta-val">' + ((inc.lat || 0).toFixed(6)) + ', ' + ((inc.lng || 0).toFixed(6)) + '</span>',
    '<span class="meta-key">Nuclear Prox.</span><span class="meta-val" style="color:' + (aiPred && aiPred.nuclearProximityAlert ? '#ff4545' : '#1de050') + '">' + (aiPred && aiPred.nuclearProximityAlert ? 'ELEVATED ALERT' : 'Clear') + '</span>',
    '<span class="meta-key">Military Corr.</span><span class="meta-val">' + ((aiPred && aiPred.militaryCorrelation) || 'None detected') + '</span>',
    '</div>',
    '<div style="margin-top:16px;padding:12px;background:rgba(8,14,10,0.5);border:1px solid #1a2e20;border-radius:8px;font-size:11px;color:#556b5e">',
    'Google Earth: <a href="https://www.google.com/maps/@' + (inc.lat||0) + ',' + (inc.lng||0) + ',12z/data=!3m1!1e3" target="_blank" style="color:#1de050">View satellite imagery</a>',
    '</div></div><hr class="separator"/>',

    // AI Assessment
    assessmentHTML,

    // Prediction
    '<div class="section">',
    '<h3>Next Event Prediction</h3>',
    '<div class="meta-grid">',
    '<span class="meta-key">Location</span><span class="meta-val">' + ((aiPred && aiPred.nextLocation && aiPred.nextLocation.name) || (pred && pred.region) || 'Computing...') + '</span>',
    '<span class="meta-key">Window</span><span class="meta-val" style="color:#1de050">' + ((aiPred && aiPred.timing && aiPred.timing.displayDate) || (pred && pred.date) || 'TBD') + '</span>',
    '<span class="meta-key">Confidence</span><span class="meta-val" style="color:#1de050">' + conf + '%</span>',
    '<span class="meta-key">Tracking ID</span><span class="meta-val">' + ((aiPred && aiPred.trackingId) || inc.tracking || 'N/A') + '</span>',
    '</div></div><hr class="separator"/>',

    // Appendix
    '<div class="section">',
    '<h3>Appendix — Data Sources</h3>',
    '<div style="font-size:11px;color:#556b5e;line-height:2">',
    dataSources.map(function(s) { return '&#8226; ' + s + '<br/>'; }).join(''),
    '</div></div>',

    // Footer
    '<div class="footer">',
    '<div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px">',
    '<div><div>PROJECT A.M.D — ALIEN DETECTION MONITOR</div><div>DEEPSPACE v4.0 — projectamd.com</div></div>',
    '<div style="text-align:right"><div>Generated: ' + now.toUTCString() + '</div><div>Tracking: ' + (inc.tracking || 'N/A') + '</div></div>',
    '</div></div>',

    '</div></div></body></html>',
  ].join('\n');
}

// ── Dossier helpers ───────────────────────────────────────────────────────────
function buildTimeline(inc) {
  const events = [];
  if (inc.date) {
    events.push({ time: inc.date + ' ' + (inc.time||''), event: `Initial observation reported — ${inc.region||'Unknown location'}`, source: inc.source?.split('·')[0]?.trim() });
  }
  if (inc.witnesses && inc.witnesses > 1) {
    events.push({ time: inc.date || 'Same date', event: `${inc.witnesses.toLocaleString()} independent witness${inc.witnesses>1?'es':''} corroborate observation`, source: 'NUFORC / Local records' });
  }
  if (inc.type === 'intercept' || inc.type === 'radar') {
    events.push({ time: inc.date || 'Same date', event: 'Military radar or intercept assets engaged', source: inc.source?.split('·')[1]?.trim() || 'DoD records' });
  }
  if (inc.sourceUrl && inc.sourceUrl.includes('aaro')) {
    events.push({ time: 'Post-event', event: 'AARO case file opened — investigation initiated', source: 'AARO — aaro.mil' });
  }
  if (inc.type === 'landing' || inc.type === 'crash') {
    events.push({ time: inc.date || 'Same date', event: 'Physical site investigation conducted — trace evidence documented', source: inc.source?.split('·')[0]?.trim() || 'Field investigation' });
  }
  events.push({ time: 'Ongoing', event: 'Case remains open in DEEPSPACE v4.0 monitoring database', source: 'A.D.M Intelligence Platform' });
  return events;
}

function getScoreRationale(label, inc, val) {
  if (label === 'Physics Anomaly') {
    if (val === 0) return 'Select incident and run AI analysis to compute score';
    if (val > 80) return `HIGH — Multiple impossible physics characteristics confirmed: ${inc.type==='intercept'?'extreme acceleration, no propulsion signature':'anomalous maneuverability'}`;
    return `Derived from acceleration reports, propulsion analysis, radar data`;
  }
  if (label === 'Incident Veracity') {
    if (val === 0) return `${inc.witnesses||0} witnesses · ${(inc.source||'').split('·').length} sources — run AI analysis to compute`;
    return `Based on ${inc.witnesses||0} witnesses, ${(inc.source||'').split('·').length} corroborating sources`;
  }
  if (label === 'Pattern Match') return `Compared against 55+ historical UAP database entries`;
  if (label === 'Geographic Risk') return `${inc.region||'Region'} — military/nuclear proximity factored`;
  return '';
}

// ── ProDashboard — shown to Pro users in the AI panel ─────────────────────────
function ProDashboard() {
  const { user } = useAuth();
  const [tab,      setTab]      = useState('sms');
  const [phone,    setPhone]    = useState('');
  const [apiKey,   setApiKey]   = useState('');
  const [zones,    setZones]    = useState([]);
  const [zoneName, setZoneName] = useState('');
  const [zoneRadius,setZoneRadius]=useState('500');
  const [msg,      setMsg]      = useState('');
  const [loading,  setLoading]  = useState(false);

  const inp = {background:'rgba(10,16,14,0.8)',border:'1px solid rgba(35,70,45,0.4)',borderRadius:6,padding:'8px 10px',color:C.text,fontSize:11,fontFamily:'inherit',width:'100%',outline:'none',boxSizing:'border-box'};
  const tabS = (id) => ({flex:1,padding:'5px 4px',border:'none',fontFamily:'inherit',cursor:'pointer',borderRadius:5,fontSize:9,fontWeight:700,letterSpacing:0.5,background:tab===id?'rgba(29,224,80,0.12)':'transparent',color:tab===id?C.green:C.dimmer,WebkitTapHighlightColor:'transparent'});

  const handleSMS = async () => {
    if (!phone) { setMsg('Enter phone number'); return; }
    setLoading(true);
    try {
      const { subscribeSMS, testSMS } = await import('./utils/api');
      await subscribeSMS({ phone, email:user?.email, tier:'pro' });
      await testSMS({ phone, email:user?.email });
      setMsg('✓ SMS active — test message sent');
    } catch(e) { setMsg('SMS setup failed — check Twilio in Railway'); }
    setLoading(false);
  };

  const handleApiKey = async () => {
    setLoading(true);
    try {
      const { getApiKey } = await import('./utils/api');
      const r = await getApiKey({ email:user?.email, tier:'pro' });
      if (r?.key) { setApiKey(r.key); setMsg('✓ API key generated'); }
    } catch(e) { setMsg('API key error'); }
    setLoading(false);
  };

  const handleAddZone = async () => {
    if (!zoneName) { setMsg('Enter zone name'); return; }
    setLoading(true);
    try {
      const { createAlertZone } = await import('./utils/api');
      const r = await createAlertZone({ email:user?.email, name:zoneName, lat:37.23, lng:-115.81, radiusKm:parseInt(zoneRadius), phone });
      if (r?.zone) { setZones(z=>[...z,r.zone]); setZoneName(''); setMsg('✓ Alert zone created'); }
    } catch(e) { setMsg('Zone creation failed'); }
    setLoading(false);
  };

  return (
    <div style={{background:'rgba(4,8,6,0.6)',border:`1px solid ${C.borderGreen}`,borderRadius:10,overflow:'hidden'}}>
      {/* Header */}
      <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:14}}>✅</span>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.green}}>Pro Active</div>
          <div style={{fontSize:9,color:C.dimmer}}>{user?.email}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:3,padding:'6px 8px',borderBottom:`1px solid ${C.border}`}}>
        <button style={tabS('sms')}    onClick={()=>setTab('sms')}>📱 SMS</button>
        <button style={tabS('api')}    onClick={()=>setTab('api')}>🔑 API</button>
        <button style={tabS('zones')}  onClick={()=>setTab('zones')}>📍 Zones</button>
        <button style={tabS('digest')} onClick={()=>setTab('digest')}>📧 Digest</button>
      </div>

      <div style={{padding:'10px 12px'}}>
        {/* SMS Setup */}
        {tab==='sms' && (
          <div>
            <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,marginBottom:8,textTransform:'uppercase'}}>SMS Alert Setup</div>
            <input style={inp} type="tel" placeholder="+1 555 000 0000" value={phone} onChange={e=>setPhone(e.target.value)}/>
            <div style={{fontSize:9,color:C.dimmer,margin:'4px 0 8px'}}>Get texted when RED threats detected</div>
            <Btn onClick={handleSMS} disabled={loading} style={{fontSize:11,padding:'8px'}}>
              {loading?<Spin/>:'Activate SMS Alerts'}
            </Btn>
            <div style={{marginTop:8,fontSize:9,color:C.dimmer}}>
              Requires: TWILIO_ACCOUNT_SID · TWILIO_AUTH_TOKEN · TWILIO_PHONE_NUMBER in Railway
            </div>
          </div>
        )}

        {/* API Access */}
        {tab==='api' && (
          <div>
            <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,marginBottom:8,textTransform:'uppercase'}}>Research API Access</div>
            <div style={{fontSize:10,color:C.dim,marginBottom:8,lineHeight:1.6}}>
              Query the A.D.M database programmatically. 10,000 requests/day.
            </div>
            {apiKey ? (
              <div>
                <div style={{background:'rgba(4,8,6,0.8)',border:`1px solid ${C.borderGreen}`,borderRadius:6,padding:'8px',marginBottom:8}}>
                  <div style={{fontSize:9,color:C.dimmer,marginBottom:3}}>YOUR API KEY</div>
                  <div style={{fontSize:9,fontFamily:'monospace',color:C.green,wordBreak:'break-all'}}>{apiKey}</div>
                </div>
                <div style={{fontSize:9,color:C.dimmer,fontFamily:'monospace',lineHeight:1.8}}>
                  GET /api/v1/sightings<br/>
                  GET /api/v1/seismic?minMag=5<br/>
                  GET /api/v1/predictions<br/>
                  GET /api/v1/solar<br/>
                  Header: X-API-Key: {apiKey.slice(0,20)}...
                </div>
              </div>
            ) : (
              <Btn onClick={handleApiKey} disabled={loading} style={{fontSize:11,padding:'8px'}}>
                {loading?<Spin/>:'Generate API Key'}
              </Btn>
            )}
          </div>
        )}

        {/* Custom Alert Zones */}
        {tab==='zones' && (
          <div>
            <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,marginBottom:8,textTransform:'uppercase'}}>Custom Alert Zones</div>
            <div style={{fontSize:10,color:C.dim,marginBottom:8}}>Get alerts only for events in your chosen areas</div>
            <input style={{...inp,marginBottom:6}} placeholder="Zone name (e.g. Nevada)" value={zoneName} onChange={e=>setZoneName(e.target.value)}/>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:8}}>
              <input style={inp} placeholder="Radius km (500)" value={zoneRadius} onChange={e=>setZoneRadius(e.target.value)}/>
              <input style={inp} type="tel" placeholder="SMS phone" value={phone} onChange={e=>setPhone(e.target.value)}/>
            </div>
            <Btn onClick={handleAddZone} disabled={loading} style={{fontSize:11,padding:'8px'}}>
              {loading?<Spin/>:'+ Add Alert Zone'}
            </Btn>
            {zones.length > 0 && (
              <div style={{marginTop:8}}>
                {zones.map(z=>(
                  <div key={z.id} style={{fontSize:10,color:C.dim,padding:'4px 0',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between'}}>
                    <span>📍 {z.name} ({z.radiusKm}km)</span>
                    <span style={{color:C.green,fontSize:9}}>ACTIVE</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Morning Digest */}
        {tab==='digest' && (
          <div>
            <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,marginBottom:8,textTransform:'uppercase'}}>Morning Intelligence Digest</div>
            <div style={{fontSize:10,color:C.dim,marginBottom:8,lineHeight:1.65}}>
              Receive a daily intelligence brief at 6:00 AM UTC covering:<br/>
              • Overnight anomalies detected<br/>
              • Seismic events with UAP correlation<br/>
              • AI predictions for next 24 hours<br/>
              • Solar/geomagnetic conditions<br/>
              • Top hotspot risk scores
            </div>
            <div style={{background:'rgba(29,224,80,0.05)',border:`1px solid ${C.borderGreen}`,borderRadius:6,padding:'8px 10px',marginBottom:8}}>
              <div style={{fontSize:10,color:C.green,fontWeight:700}}>✓ Digest Active</div>
              <div style={{fontSize:9,color:C.dimmer,marginTop:2}}>Sent to: {user?.email}</div>
              <div style={{fontSize:9,color:C.dimmer}}>Next: 06:00 UTC</div>
            </div>
            <div style={{fontSize:9,color:C.dimmer}}>
              Requires: SENDGRID_API_KEY + FROM_EMAIL in Railway
            </div>
          </div>
        )}

        {msg && (
          <div style={{marginTop:8,fontSize:10,color:msg.startsWith('✓')?C.green:C.red,padding:'6px 8px',background:msg.startsWith('✓')?'rgba(29,224,80,0.06)':'rgba(255,69,69,0.06)',borderRadius:5,border:`1px solid ${msg.startsWith('✓')?C.borderGreen:'rgba(255,69,69,0.2)'}`}}>
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AI PANEL CONTENT
// ═══════════════════════════════════════════════════════════════════════════════
function AIPanelContent({ aiPred, aiLoading, selected, onProClick, isPro, onShowPredictionPath }) {
  const pred = PREDICTIONS[0];
  const [exporting,  setExporting]  = useState(false);
  const [activeTab,  setActiveTab]  = useState('prediction');

  const Row = ({k,v,vc,mono}) => (
    <div style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)',gap:10}}>
      <span style={{fontSize:11,color:C.dimmer,flexShrink:0}}>{k}</span>
      <span style={{fontSize:11,color:vc||C.dim,textAlign:'right',fontVariantNumeric:'tabular-nums',fontFamily:mono?'monospace':'inherit'}}>{v}</span>
    </div>
  );

  const ScoreBar = ({label,value,color}) => (
    <div style={{marginBottom:8}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
        <span style={{fontSize:10,color:C.dimmer}}>{label}</span>
        <span style={{fontSize:10,color:color||C.green,fontWeight:700}}>{value}%</span>
      </div>
      <div style={{height:3,background:'rgba(255,255,255,0.06)',borderRadius:2,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${value}%`,background:color||C.green,borderRadius:2,transition:'width 1s ease'}}/>
      </div>
    </div>
  );

  const handleExport = async () => {
    if (!isPro) { onProClick('export'); return; }
    setExporting(true);
    const inc = selected;
    if (!inc) { setExporting(false); return; }

    const now       = new Date();
    const conf      = aiPred?.confidence || pred?.confidence || 87;
    const physScore = aiPred?.physicsAnomalyScore || 0;
    const verScore  = aiPred?.incidentVeracity    || 0;
    const patScore  = aiPred?.patternMatch        || conf;
    const geoScore  = aiPred?.geographicRiskFactor|| 50;

    // Evidence sources from the incident data
    const sources  = (inc.source || '').split('·').map(s => s.trim()).filter(Boolean);
    const docs     = getDocumentLinks(inc); // Real FOIA document links

    const html = buildDossierHTML(inc, aiPred, pred, conf, physScore, verScore, patScore, geoScore, sources, docs, now);
        // UTF-8 BOM prevents encoding issues on all browsers/OS
    const BOM  = '\uFEFF';
    const blob = new Blob([BOM + html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);

    // Open in new tab so user sees the full dossier immediately
    const newTab = window.open(url, '_blank');
    if (!newTab) {
      // Fallback to download if popup blocked
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `ADM_DOSSIER_${(inc.name||'incident').replace(/[^a-z0-9]/gi,'_').slice(0,40)}_${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}.html`;
      a.click();
    }
    // Also trigger download
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `ADM_DOSSIER_${(inc.name||'incident').replace(/[^a-z0-9]/gi,'_').slice(0,40)}_${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}.html`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    showToast('✓ Intelligence dossier downloaded','success');
    setExporting(false);
  };

  const handleShare = async () => {
    if (!isPro) { onProClick('share'); return; }
    const inc  = selected;
    const text = `🛸 DEEPSPACE v4.0 Intel\n\n📍 ${inc?.name||'UAP'}\n🗺 ${inc?.region||'Unknown'}\n\n🔬 Physics Score: ${aiPred?.physicsAnomalyScore||'N/A'}/100\n🎯 Confidence: ${aiPred?.confidence||85}%\n⚠ Threat: ${aiPred?.threatLevel||'AMBER'}\n🤖 Next: ${aiPred?.nextLocation?.name||'Unknown'}\n\n📚 ${inc?.sourceUrl||'https://www.aaro.mil'}`;
    if (navigator.share) {
      try { await navigator.share({title:`DEEPSPACE: ${inc?.name||'UAP'}`,text,url:inc?.sourceUrl||'https://www.aaro.mil'}); }
      catch(_){}
    } else {
      try { await navigator.clipboard.writeText(text); showToast('✓ Copied','success'); }
      catch(_) { showToast('Share not available','error'); }
    }
  };

  const confidence  = aiPred?.confidence || pred?.confidence || 87;
  const threatColor = (t) => t==='RED'?C.red:t==='AMBER'?C.amber:C.green;
  const tabStyle    = (id) => ({
    padding:'6px 12px', fontSize:11, fontWeight:600, cursor:'pointer',
    borderRadius:6, border:'none', fontFamily:'inherit',
    background:  activeTab===id ? 'rgba(29,224,80,0.15)' : 'transparent',
    color:       activeTab===id ? C.green : C.dimmer,
    WebkitTapHighlightColor:'transparent',
  });

  return (
    <div>
      {/* Engine status bar */}
      <div style={{padding:'12px 18px',borderBottom:`1px solid ${C.border}`,background:'rgba(4,8,6,0.5)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
          <div style={{display:'flex',alignItems:'center',gap:7}}>
            <Pulse level="GREEN"/>
            <span style={{fontSize:11,fontWeight:800,letterSpacing:2,color:C.green,textTransform:'uppercase'}}>DEEPSPACE v4.0</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:5}}>
            {aiLoading ? (
              <>
                <Spin/>
                <span style={{fontSize:9,color:C.amber,fontWeight:700,letterSpacing:1}}>AI ENRICHING...</span>
              </>
            ) : aiPred?.isInstant ? (
              <>
                <div style={{width:6,height:6,borderRadius:'50%',background:C.amber,animation:'adm-pulse 1s infinite'}}/>
                <span style={{fontSize:9,color:C.amber,fontWeight:700,letterSpacing:1}}>INSTANT SCORES</span>
              </>
            ) : (
              <>
                <div style={{width:6,height:6,borderRadius:'50%',background:C.green,animation:'adm-pulse 1.5s infinite'}}/>
                <span style={{fontSize:9,color:C.green,fontWeight:700,letterSpacing:1}}>AI COMPLETE</span>
              </>
            )}
          </div>
        </div>
        <div style={{display:'flex',gap:8,fontSize:10,color:C.dimmer,flexWrap:'wrap'}}>
          <span>⚡ {aiPred?.engineCycle||0} cycles</span>
          <span>·</span>
          <span>🌍 Kp {aiPred?.solarConditions?.kpIndex||'—'}</span>
          <span>·</span>
          <span style={{color:threatColor(aiPred?.globalThreatLevel||'AMBER'),fontWeight:700}}>
            {aiPred?.globalThreatLevel||'AMBER'} ALERT
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,padding:'8px 14px',borderBottom:`1px solid ${C.border}`}}>
        <button style={tabStyle('prediction')} onClick={()=>setActiveTab('prediction')}>Prediction</button>
        <button style={tabStyle('analysis')}   onClick={()=>setActiveTab('analysis')}>Analysis</button>
        <button style={tabStyle('threats')}    onClick={()=>setActiveTab('threats')}>Threats</button>
      </div>

      {/* PREDICTION TAB */}
      {activeTab === 'prediction' && (
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontSize:10,letterSpacing:1.5,color:C.dimmer,fontWeight:600,marginBottom:8,textTransform:'uppercase'}}>Next Predicted Event</div>
          {aiLoading
            ? <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 0'}}>
                <Spin/>
                <div>
                  <div style={{fontSize:12,color:C.green,fontWeight:600}}>DEEPSPACE computing...</div>
                  <div style={{fontSize:10,color:C.dimmer,marginTop:2}}>Cross-referencing 80+ years of data</div>
                </div>
              </div>
            : <>
                <div style={{display:'flex',gap:12,marginBottom:12,padding:'12px',background:'rgba(8,14,10,0.6)',borderRadius:8,border:'1px solid rgba(29,224,80,0.15)'}}>
                  <div style={{width:56,height:40,background:'rgba(12,19,15,0.8)',borderRadius:7,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><UFOSVG/></div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:800,color:C.text,lineHeight:1.2}}>{aiPred?.nextLocation?.name||pred?.region?.split(',')[0]||'Analyzing...'}</div>
                    <div style={{fontSize:11,color:C.dim,margin:'3px 0'}}>{aiPred?.nextLocation?.region||'Global monitoring active'}</div>
                    <div style={{fontSize:10,color:C.dimmer,fontFamily:'monospace'}}>{aiPred?.nextLocation?.coords||'Coordinates computing...'}</div>
                  </div>
                  {aiPred?.nuclearProximityAlert && (
                    <div style={{flexShrink:0,fontSize:9,padding:'3px 6px',background:'rgba(255,69,69,0.15)',border:'1px solid rgba(255,69,69,0.4)',borderRadius:4,color:C.red,fontWeight:700}}>☢ NUC</div>
                  )}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                  <div style={{background:'rgba(8,14,10,0.5)',border:'1px solid rgba(29,224,80,0.1)',borderRadius:7,padding:'9px'}}>
                    <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:3}}>Window Opens</div>
                    <div style={{fontSize:13,fontWeight:700,color:C.green}}>{aiPred?.timing?.displayDate||pred?.date||'Analyzing'}</div>
                    <div style={{fontSize:10,color:C.dim,marginTop:1}}>{aiPred?.timing?.displayTime||pred?.time||'UTC'}</div>
                  </div>
                  <div style={{background:'rgba(8,14,10,0.5)',border:'1px solid rgba(29,224,80,0.1)',borderRadius:7,padding:'9px'}}>
                    <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:3}}>Duration</div>
                    <div style={{fontSize:13,fontWeight:700,color:C.amber}}>{aiPred?.timing?.windowHours||12}h</div>
                    <div style={{fontSize:10,color:C.dim,marginTop:1}}>Monitoring window</div>
                  </div>
                </div>
                <div style={{marginBottom:12}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                    <span style={{fontSize:10,color:C.dimmer,textTransform:'uppercase',letterSpacing:1}}>AI Confidence</span>
                    <span style={{fontSize:20,fontWeight:800,color:C.green}}>{confidence}%</span>
                  </div>
                  <div style={{height:6,background:'rgba(18,36,22,0.8)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${confidence}%`,background:`linear-gradient(90deg,#0ea043,${C.green},#00ff88)`,borderRadius:3,transition:'width 1.5s ease',boxShadow:'0 0 8px rgba(29,224,80,0.4)'}}/>
                  </div>
                </div>
                {aiPred?.basisFactors?.filter(Boolean).length > 0 && (
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>Prediction Basis</div>
                    {aiPred.basisFactors.filter(Boolean).slice(0,4).map((f,i)=>(
                      <div key={i} style={{display:'flex',gap:6,marginBottom:3}}>
                        <span style={{color:C.green,fontSize:10,flexShrink:0}}>▸</span>
                        <span style={{fontSize:11,color:C.dim,lineHeight:1.4}}>{f}</span>
                      </div>
                    ))}
                  </div>
                )}
                <Btn onClick={onShowPredictionPath}>▶ Show Prediction Path on Globe</Btn>

                {/* Live signal strength chart */}
                <SignalChart confidence={confidence} time={Date.now()}/>
              </>
          }
        </div>
      )}

      {/* ANALYSIS TAB */}
      {activeTab === 'analysis' && (
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`}}>
          {!aiPred || aiLoading
            ? <div style={{textAlign:'center',padding:'20px 0',color:C.dimmer,fontSize:12}}>
                {aiLoading ? 'SENTINEL analyzing incident...' : 'Select an incident to begin analysis'}
              </div>
            : <>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Anomaly Scores</div>
                  <ScoreBar label="Physics Anomaly"   value={aiPred.physicsAnomalyScore||0}   color={aiPred.physicsAnomalyScore>70?C.red:C.amber}/>
                  <ScoreBar label="Incident Veracity" value={aiPred.incidentVeracity||0}      color={C.green}/>
                  <ScoreBar label="Pattern Match"     value={aiPred.patternMatch||confidence} color={C.green}/>
                  <ScoreBar label="Geographic Risk"   value={aiPred.geographicRiskFactor||50} color={C.amber}/>
                </div>
                {aiPred.propulsionSignature && (
                  <div style={{background:'rgba(8,14,10,0.6)',border:'1px solid rgba(29,224,80,0.15)',borderRadius:7,padding:'10px 12px',marginBottom:10}}>
                    <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:3}}>Propulsion Signature</div>
                    <div style={{fontSize:12,color:C.green,fontWeight:600}}>{aiPred.propulsionSignature}</div>
                  </div>
                )}
                {aiPred.scientificAnalysis && (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>Scientific Analysis</div>
                    <div style={{fontSize:11,color:C.dim,lineHeight:1.65,background:'rgba(4,8,6,0.5)',padding:'10px 12px',borderRadius:7,border:`1px solid ${C.border}`}}>{aiPred.scientificAnalysis}</div>
                  </div>
                )}
                {aiPred.classifiedAssessment && (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:10,color:C.red,letterSpacing:1,textTransform:'uppercase',marginBottom:5,fontWeight:700}}>⚠ Classified Assessment</div>
                    <div style={{fontSize:11,color:'rgba(200,160,160,0.8)',lineHeight:1.65,background:'rgba(255,69,69,0.06)',padding:'10px 12px',borderRadius:7,border:'1px solid rgba(255,69,69,0.15)'}}>{aiPred.classifiedAssessment}</div>
                  </div>
                )}
                {aiPred.historicalPrecedent && (
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>Historical Precedent</div>
                    <div style={{fontSize:11,color:C.dim,lineHeight:1.65}}>{aiPred.historicalPrecedent}</div>
                  </div>
                )}
                <Row k="Military Corr."    v={aiPred.militaryCorrelation||'None detected'}/>
                <Row k="Nuclear Proximity" v={aiPred.nuclearProximityAlert?'⚠ ALERT':'Clear'} vc={aiPred.nuclearProximityAlert?C.red:C.green}/>
                <Row k="Tracking ID"       v={aiPred.trackingId||selected?.tracking||'Generating...'} vc={C.green} mono/>
                {aiPred.recommendedActions?.length > 0 && (
                  <div style={{marginTop:10}}>
                    <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>Recommended Actions</div>
                    {aiPred.recommendedActions.slice(0,3).map((a,i)=>(
                      <div key={i} style={{display:'flex',gap:6,marginBottom:4,fontSize:11,color:C.dim}}>
                        <span style={{color:C.amber,flexShrink:0}}>→</span>{a}
                      </div>
                    ))}
                  </div>
                )}
              </>
          }
          <div style={{display:'flex',gap:7,marginTop:12}}>
            <button onClick={handleExport} disabled={exporting} style={{flex:1,padding:'9px 0',background:isPro?'rgba(29,224,80,0.08)':'rgba(255,255,255,0.03)',border:`1px solid ${isPro?C.borderGreen:C.border}`,borderRadius:7,color:isPro?C.green:C.dimmer,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:5,WebkitTapHighlightColor:'transparent'}}>
              {exporting?<Spin/>:'↓'} {isPro?'Export':'🔒 Export'}
            </button>
            <button onClick={handleShare} style={{flex:1,padding:'9px 0',background:isPro?'rgba(29,224,80,0.08)':'rgba(255,255,255,0.03)',border:`1px solid ${isPro?C.borderGreen:C.border}`,borderRadius:7,color:isPro?C.green:C.dimmer,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:5,WebkitTapHighlightColor:'transparent'}}>
              ⇗ {isPro?'Share':'🔒 Share'}
            </button>
          </div>
          {!isPro&&<div style={{marginTop:6,fontSize:10,color:C.dimmer,textAlign:'center'}}>Pro required · <span onClick={()=>onProClick('default')} style={{color:C.amber,cursor:'pointer',fontWeight:600}}>Upgrade →</span></div>}
        </div>
      )}

      {/* THREATS TAB — probability simulation + AI discoveries */}
      {activeTab === 'threats' && (
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`}}>

          {/* AI Current Focus */}
          {aiPred?.activeAlerts?.[0] && (
            <div style={{background:'rgba(29,224,80,0.06)',border:`1px solid ${C.borderGreen}`,borderRadius:8,padding:'10px 12px',marginBottom:10}}>
              <div style={{fontSize:9,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:4}}>🧠 AI Currently Focusing On</div>
              <div style={{fontSize:12,fontWeight:700,color:C.green}}>{aiPred.activeAlerts[0].name}</div>
              <div style={{fontSize:10,color:C.dim,marginTop:2}}>Risk: {aiPred.activeAlerts[0].risk}% · AI Directed</div>
            </div>
          )}

          {/* Probability Simulation */}
          <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Probability Simulation</div>
          {aiPred?.activeAlerts?.length > 0 ? aiPred.activeAlerts.map((alert,i)=>(
            <div key={i} style={{marginBottom:6}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:2}}>
                <span style={{fontSize:10,color:C.text,fontWeight:600}}>{alert.name}</span>
                <span style={{fontSize:10,color:threatColor(alert.level),fontWeight:700}}>{alert.risk}%</span>
              </div>
              <div style={{height:3,background:'rgba(255,255,255,0.06)',borderRadius:2,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${alert.risk}%`,background:threatColor(alert.level),borderRadius:2,transition:'width 1s ease'}}/>
              </div>
              <div style={{fontSize:8,color:C.dimmer,marginTop:2}}>
                {alert.nuclear ? '☢ Nuclear · ' : ''}{alert.level}
              </div>
            </div>
          )) : (
            <div style={{fontSize:11,color:C.dimmer,marginBottom:10}}>Computing simulation...</div>
          )}

          {/* Solar Conditions */}
          {aiPred?.solarConditions && (
            <div style={{background:'rgba(8,14,10,0.6)',border:'1px solid rgba(29,224,80,0.15)',borderRadius:8,padding:'10px 12px',marginBottom:10,marginTop:12}}>
              <div style={{fontSize:10,color:C.dimmer,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Real NOAA Solar Data</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                {[['Kp-Index',aiPred.solarConditions.kpIndex],['Solar Flux',aiPred.solarConditions.solarFlux],['Lunar Phase',aiPred.solarConditions.lunarPhase],['Night Window',aiPred.solarConditions.isNightWindow?'ACTIVE':'Inactive']].map(([k,v])=>(
                  <div key={k} style={{textAlign:'center',padding:'6px',background:'rgba(4,8,6,0.5)',borderRadius:5}}>
                    <div style={{fontSize:9,color:C.dimmer,marginBottom:2}}>{k}</div>
                    <div style={{fontSize:12,fontWeight:700,color:C.green}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Row k="Global Threat"  v={aiPred?.globalThreatLevel||'AMBER'}  vc={threatColor(aiPred?.globalThreatLevel||'AMBER')}/>
          <Row k="Anomaly Score"  v={`${aiPred?.anomalyScore||50}/100`}   vc={aiPred?.anomalyScore>70?C.red:C.amber}/>
          <Row k="Engine Cycles"  v={aiPred?.engineCycle||0}/>
        </div>
      )}

      {/* Pro CTA / Pro Dashboard */}
      <div style={{padding:'14px 18px'}}>
        {isPro ? (
          <ProDashboard user={selected}/>
        ) : (
          <div style={{background:'rgba(255,170,0,0.06)',border:'1px solid rgba(255,170,0,0.2)',borderRadius:10,padding:'13px'}}>
            <div style={{fontSize:12,fontWeight:700,color:C.amber,marginBottom:6}}>⚡ Unlock Full DEEPSPACE v4.0</div>
            <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:12}}>
              {['AI analysis — instant scores','Intelligence dossier exports','Real FOIA document links','SMS alerts — unlimited','Custom alert zones','Morning email digest','API access for researchers'].map(f=>(
                <div key={f} style={{display:'flex',alignItems:'center',gap:6,fontSize:10,color:C.dim}}>
                  <span style={{color:C.amber,fontSize:9}}>✓</span>{f}
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:8}}>
              <button onClick={()=>onProClick('alert')} style={{padding:'9px',background:'rgba(255,170,0,0.1)',border:'1px solid rgba(255,170,0,0.3)',borderRadius:7,color:C.amber,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',WebkitTapHighlightColor:'transparent'}}>
                Pro $199/mo
              </button>
              <button onClick={()=>onProClick('alert')} style={{padding:'9px',background:'rgba(29,224,80,0.07)',border:`1px solid ${C.borderGreen}`,borderRadius:7,color:C.green,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',WebkitTapHighlightColor:'transparent'}}>
                Basic $19.99/mo
              </button>
            </div>
            <div style={{fontSize:9,color:C.dimmer,textAlign:'center'}}>Annual plans available · Cancel anytime</div>
          </div>
        )}
      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════════
// SUCCESS PAGE
// ═══════════════════════════════════════════════════════════════════════════════
function SuccessPage() {
  const { refreshProStatus, user } = useAuth();
  const [status, setStatus] = useState('verifying');

  useEffect(()=>{
    const activate = async () => {
      try {
        const params    = new URLSearchParams(window.location.search);
        const sessionId = params.get('session');
        if (sessionId) {
          // Verify session with backend — grants Pro directly, no webhook needed
          try {
            const res  = await fetch(
              `${process.env.REACT_APP_API_URL}/api/payments/verify-session`,
              { method:'POST', headers:{'Content-Type':'application/json'},
                body: JSON.stringify({ sessionId, email: user?.email||'' }) }
            );
            const data = await res.json();
            if (data?.paid) { await refreshProStatus(); setStatus('success'); }
            else { await refreshProStatus(); setStatus('success'); }
          } catch(_) { await refreshProStatus(); setStatus('success'); }
        } else {
          await refreshProStatus(); setStatus('success');
        }
      } catch(_) { setStatus('success'); }
      setTimeout(()=>window.location.href='/', 3500);
    };
    activate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  return (
    <div style={{width:'100vw',height:'100vh',background:'#07090c',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'#cdd9d2',padding:24,gap:16,fontFamily:'-apple-system,sans-serif'}}>
      {status==='verifying' ? (
        <>
          <div style={{width:44,height:44,border:'3px solid rgba(29,224,80,0.2)',borderTop:'3px solid #1de050',borderRadius:'50%',animation:'adm-spin 0.8s linear infinite'}}/>
          <div style={{fontSize:15,color:'rgba(140,175,150,0.6)'}}>Activating Pro access...</div>
        </>
      ) : (
        <>
          <div style={{fontSize:64}}>🛸</div>
          <div style={{fontSize:28,fontWeight:700,color:'#1de050'}}>Pro Activated</div>
          <div style={{fontSize:14,color:'rgba(155,182,162,0.55)',textAlign:'center'}}>
            Welcome to A.D.M Pro.<br/>Full access on all devices. Redirecting...
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN GLOBE APP
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// SIGHTINGS TAB PANEL — Live UAP + Seismic + Atmospheric events
// ═══════════════════════════════════════════════════════════════════════════════
function SightingsTabPanel({ sightings, seismicEvents, atmosphericEvents, selected, query, onSelect }) {
  const [tab, setTab] = useState('uap');
  const q = query.toLowerCase();

  const tabBtn = (id, label, count, color) => (
    <button onClick={()=>setTab(id)} style={{
      flex:1, padding:'6px 4px', border:'none', fontFamily:'inherit',
      background: tab===id ? `${color}18` : 'transparent',
      borderBottom: tab===id ? `2px solid ${color}` : '2px solid transparent',
      color: tab===id ? color : 'rgba(120,150,130,0.5)',
      fontSize:9, fontWeight:700, cursor:'pointer', letterSpacing:0.8,
      WebkitTapHighlightColor:'transparent', textTransform:'uppercase',
    }}>
      {label} {count>0&&<span style={{fontSize:8,opacity:0.7}}>({count})</span>}
    </button>
  );

  const major   = seismicEvents.filter(s=>(s.magnitude||0)>=5.0);
  const allSeis = seismicEvents.filter(s=>!q||s.name?.toLowerCase().includes(q)||s.region?.toLowerCase().includes(q));
  const allAtmo = atmosphericEvents.filter(a=>!q||a.name?.toLowerCase().includes(q)||a.region?.toLowerCase().includes(q));
  const allUAP  = sightings.filter(s=>!q||s.name?.toLowerCase().includes(q)||s.region?.toLowerCase().includes(q));

  const ItemRow = ({ inc, icon }) => (
    <div onClick={()=>onSelect(inc)} style={{
      display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
      borderBottom:`1px solid ${C.border}`,
      borderLeft:`3px solid ${selected?.id===inc.id?tc(inc.threatLevel):'transparent'}`,
      background: selected?.id===inc.id?`${tc(inc.threatLevel)}0a`:'transparent',
      cursor:'pointer', transition:'all 0.14s', WebkitTapHighlightColor:'transparent',
    }}
      onMouseEnter={e=>{ if(selected?.id!==inc.id) e.currentTarget.style.background='rgba(29,224,80,0.04)'; }}
      onMouseLeave={e=>{ if(selected?.id!==inc.id) e.currentTarget.style.background='transparent'; }}
    >
      <div style={{fontSize:16,flexShrink:0}}>{icon}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:600,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{inc.name}</div>
        <div style={{fontSize:10,color:C.dim,marginTop:2}}>{inc.region}</div>
        <div style={{display:'flex',gap:8,marginTop:2,alignItems:'center'}}>
          <span style={{fontSize:9,color:C.dimmer,fontVariantNumeric:'tabular-nums'}}>{inc.date}</span>
          {inc.magnitude && <span style={{fontSize:9,color:inc.magnitude>=6?C.red:C.amber,fontWeight:700}}>M{inc.magnitude?.toFixed(1)}</span>}
          {inc.source && <span style={{fontSize:9,color:C.dimmer,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:80}}>{inc.source?.split('·')[0]?.split('—')[0]?.trim()}</span>}
        </div>
      </div>
      <Dot level={inc.threatLevel}/>
    </div>
  );

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
      {/* Tabs */}
      <div style={{display:'flex',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
        {tabBtn('uap',      'UAP',       allUAP.length,  C.green)}
        {tabBtn('seismic',  'Seismic',   allSeis.length, C.amber)}
        {tabBtn('atmo',     'Earth',     allAtmo.length, '#00cfff')}
      </div>
      {/* Live status bar */}
      <div style={{padding:'5px 14px',borderBottom:`1px solid ${C.border}`,background:'rgba(4,8,6,0.4)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:5}}>
          <div style={{width:5,height:5,borderRadius:'50%',background:C.green,animation:'adm-pulse 1.5s infinite'}}/>
          <span style={{fontSize:9,fontFamily:'monospace',color:'rgba(0,200,100,0.6)',letterSpacing:1}}>LIVE DATA</span>
        </div>
        {tab==='seismic' && major.length>0 && (
          <span style={{fontSize:9,color:C.red,fontWeight:700,animation:'adm-blink 1s infinite'}}>
            ⚠ {major.length} M5.0+ ACTIVE
          </span>
        )}
        {tab==='seismic' && (
          <span style={{fontSize:9,color:C.dimmer}}>USGS REAL-TIME</span>
        )}
        {tab==='atmo' && (
          <span style={{fontSize:9,color:C.dimmer}}>NASA EONET</span>
        )}
        {tab==='uap' && (
          <span style={{fontSize:9,color:C.dimmer}}>AARO · NUFORC · DoD</span>
        )}
      </div>

      {/* List */}
      <div style={{flex:1,overflowY:'auto'}}>
        {tab==='uap' && (
          <>
            {allUAP.map(inc => (
              <ItemRow key={inc.id} inc={inc} icon={<AlienSVG size={18} color={tc(inc.threatLevel)}/>}/>
            ))}
            <div style={{padding:'10px 16px',fontSize:10,color:C.dimmer,lineHeight:1.6,borderTop:`1px solid ${C.border}`}}>
              {allUAP.length} incidents · Sources: AARO · FBI · NARA · NSA · NUFORC
            </div>
          </>
        )}
        {tab==='seismic' && (
          <>
            {allSeis.length === 0 && (
              <div style={{padding:'20px 16px',textAlign:'center'}}>
                <div style={{fontSize:28,marginBottom:8}}>🌍</div>
                <div style={{fontSize:12,color:C.dim,marginBottom:4}}>Fetching USGS real-time data...</div>
                <div style={{fontSize:10,color:C.dimmer}}>
                  Backend must be running on Railway.<br/>
                  Check: <span style={{color:C.green}}>railway.app dashboard</span>
                </div>
              </div>
            )}
            {allSeis.map(s => (
              <ItemRow key={s.id} inc={s} icon={
                <div style={{width:20,height:20,borderRadius:'50%',border:`2px solid ${s.magnitude>=6?C.red:s.magnitude>=4.5?C.amber:'rgba(255,200,50,0.7)'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,color:s.magnitude>=6?C.red:C.amber}}>
                  {s.magnitude?.toFixed(0)||'?'}
                </div>
              }/>
            ))}
            {allSeis.length > 0 && (
              <div style={{padding:'10px 16px',fontSize:10,color:C.dimmer,lineHeight:1.6,borderTop:`1px solid ${C.border}`}}>
                {allSeis.length} seismic events (M2.5+) · Last hour · USGS Real-time API
              </div>
            )}
          </>
        )}
        {tab==='atmo' && (
          <>
            {allAtmo.length === 0 && (
              <div style={{padding:'20px 16px',textAlign:'center',fontSize:12,color:C.dimmer}}>Loading NASA EONET data...</div>
            )}
            {allAtmo.map(a => (
              <ItemRow key={a.id} inc={a} icon={
                a.eventType==='volcanic'?'🌋':a.eventType==='storm'?'⛈':a.eventType==='wildfire'?'🔥':'🌊'
              }/>
            ))}
            {allAtmo.length > 0 && (
              <div style={{padding:'10px 16px',fontSize:10,color:C.dimmer,lineHeight:1.6,borderTop:`1px solid ${C.border}`}}>
                {allAtmo.length} active events · NASA EONET Earth Observatory
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
function GlobeApp() {
  const globeRef  = useRef(null);
  const searchRef = useRef(0);
  const bannerDismissed = useRef(
    localStorage.getItem('adm_banner_dismissed') === 'true'
  );

  const { user, isPro, loading: authLoading, refreshProStatus } = useAuth();

  const [selected,      setSelected]      = useState(null);
  const [aiPred,        setAiPred]        = useState(null);
  const [aiLoading,     setAiLoading]     = useState(false);
  const [showLeft,      setShowLeft]      = useState(false);
  const [showRight,     setShowRight]     = useState(false);
  const [showDonation,  setShowDonation]  = useState(false);
  const [showPro,       setShowPro]       = useState(false);
  const [proTrigger,    setProTrigger]    = useState('default');
  const [showAuth,      setShowAuth]      = useState(false);
  const [authMode,      setAuthMode]      = useState('signin');
  const [showStreet,    setShowStreet]    = useState(false);
  const [streetTarget,  setStreetTarget]  = useState(null);
  const [showOnboarding,setShowOnboarding]= useState(false);
  const [showBanner,    setShowBanner]    = useState(false);
  const [query,         setQuery]         = useState('');
  const [results,       setResults]       = useState([]);
  const [showResults,   setShowResults]   = useState(false);

  // ── DEEPSPACE v4.0 live intelligence state ────────────────────────────────
  const [livePredictions, setLivePredictions] = useState([]);
  const [globalThreat,    setGlobalThreat]    = useState('AMBER');
  const [liveThreats,     setLiveThreats]     = useState([]);
  const [seismicEvents,   setSeismicEvents]   = useState([]);
  const [atmosphericEvts, setAtmosphericEvts] = useState([]);
  const [realSolarData,   setRealSolarData]   = useState(null);

  // ── COGNITION ENGINE state — AI autonomous control ────────────────────────
  const [aiThoughts,      setAiThoughts]      = useState([]);
  const [aiDiscoveries,   setAiDiscoveries]   = useState([]);
  const [aiFocus,         setAiFocus]         = useState(null);
  const lastAIZoom = useRef(0);

  const [activeLayers,    setActiveLayers]    = useState(['historical','signal']);
  const [showLayers,      setShowLayers]      = useState(false);
  const enginePollRef = useRef(null);

  // Autonomous zoom — AI takes over occasionally
  useAutonomousZoom(globeRef, SIGHTINGS, globalThreat);

  const { alerts, alertCount } = useWebSocket();

  // ── Show onboarding to brand-new visitors ─────────────────────────────────
  useEffect(()=>{
    if (authLoading) return;
    const seen = localStorage.getItem('adm_onboarded');
    if (!seen && !user) {
      // Delay 1.5s so globe loads first — they see the Earth spinning then get prompted
      const t = setTimeout(()=>setShowOnboarding(true), 1500);
      return ()=>clearTimeout(t);
    }
  },[authLoading, user]);

  // ── Show support bar to free users — 45s delay, once per day ─────────────
  useEffect(()=>{
    if (isPro || bannerDismissed.current) return;
    const lastShown   = localStorage.getItem('adm_support_bar_last');
    const hoursSince  = lastShown ? (Date.now()-parseInt(lastShown))/3600000 : 999;
    if (hoursSince < 20) return;
    const t = setTimeout(()=>{ if(!isPro) setShowBanner(true); }, 45000);
    return ()=>clearTimeout(t);
  },[isPro]);

  // ── Wake Railway backend immediately on app load ─────────────────────────
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL;
    if (base) {
      // Ping immediately to wake Railway from cold start
      fetch(`${base}/api/sightings/stats/summary`).catch(() => {});
      // Second ping after 3s in case first didn't wake it
      setTimeout(() => {
        fetch(`${base}/api/sightings/stats/summary`).catch(() => {});
      }, 3000);
    }
  }, []);

  // ── Stripe return ─────────────────────────────────────────────────────────
  useEffect(()=>{
    const p = new URLSearchParams(window.location.search);
    if (p.get('donated')==='true'||p.get('session')){
      window.history.replaceState({},'','/');
      refreshProStatus().then(()=>showToast('🛸 Pro access activated!','success'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // ── DEEPSPACE v4.0 — continuous live intelligence polling ─────────────────
  // Polls every 30 seconds to get fresh predictions, threats, solar data
  useEffect(()=>{
    const poll = async () => {
      try {
        const [predsRes, threatsRes, liveRes, cognitionRes] = await Promise.allSettled([
          getAIPredictions(),
          getAIThreats(),
          getLiveFeed(),
          getCognitionState(),
        ]);
        if (predsRes.status==='fulfilled' && predsRes.value?.predictions?.length > 0) {
          const newThreat = predsRes.value.riskLevel || 'AMBER';
          if (newThreat === 'RED' && globalThreat !== 'RED') SoundSystem.threatEscalation();
          else if (newThreat !== globalThreat) SoundSystem.alert(newThreat);
          setLivePredictions(predsRes.value.predictions);
          setGlobalThreat(newThreat);
        }
        if (threatsRes.status==='fulfilled') {
          const newThreats = threatsRes.value?.activeAlerts || [];
          if(newThreats.length > liveThreats.length) SoundSystem.dataReceived();
          setLiveThreats(newThreats);
        }
        if (liveRes.status==='fulfilled') {
          const feed = liveRes.value;
          if (feed?.seismic?.length)    setSeismicEvents(feed.seismic);
          if (feed?.atmospheric?.length)setAtmosphericEvts(feed.atmospheric);
          if (feed?.solar)              setRealSolarData(feed.solar);
          const majorQuake = feed?.seismic?.find(s => (s.magnitude||0) >= 6.0);
          if (majorQuake) SoundSystem.alert('RED');
        }

        // ── AI AUTONOMOUS COGNITION — AI directs the globe ───────────────────
        if (cognitionRes.status==='fulfilled') {
          const cog = cognitionRes.value;
          if (cog.thoughtStream?.length)  setAiThoughts(cog.thoughtStream);
          if (cog.discoveries?.length)    setAiDiscoveries(cog.discoveries);
          if (cog.currentFocus)           setAiFocus(cog.currentFocus);

          // AI TAKES OVER THE GLOBE — moves camera to where AI is focusing
          const focus = cog.currentFocus;
          const now   = Date.now();
          if (
            focus?.lat && focus?.lng &&
            globeRef.current &&
            now - lastAIZoom.current > 90000 && // Min 90s between AI zooms
            !showRight && !showLeft && !showStreet // Don't interrupt user
          ) {
            lastAIZoom.current = now;
            showToast(`🧠 AI focusing on ${focus.name}`, 'info');
            globeRef.current.zoomTo(focus.lat, focus.lng, 4); // Gentle overview
            // Return to global after 8 seconds
            setTimeout(() => {
              if (globeRef.current) globeRef.current.resetView();
            }, 8000);
          }
        }
      } catch(_) {}
    };

    // ── Keep Railway warm — ping every 25 minutes to prevent cold starts ────
    const keepWarm = setInterval(() => {
      const base = process.env.REACT_APP_API_URL;
      if (base) fetch(`${base}/api/sightings/stats/summary`, { method:'GET' }).catch(()=>{});
    }, 25 * 60 * 1000); // 25 minutes

    // Initial poll after 3 seconds (let engine boot)
    const init = setTimeout(poll, 3000);
    // Then every 30 seconds
    enginePollRef.current = setInterval(poll, 30000);
    return () => {
      clearTimeout(init);
      clearInterval(enginePollRef.current);
      clearInterval(keepWarm);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPro = useCallback((trigger='default')=>{
    // Never prompt existing Pro users to upgrade again
    if (isPro) {
      showToast('✅ You already have Pro access!','success');
      return;
    }
    setProTrigger(trigger);
    setShowRight(false);
    if (!user) {
      setAuthMode('signup');
      setShowAuth(true);
    } else {
      setShowPro(true);
    }
  },[user, isPro]);

  // ── Search ────────────────────────────────────────────────────────────────
  const doSearch = useCallback((q)=>{
    searchRef.current++;
    if(searchRef.current>=3&&!isPro) setShowDonation(true);
    if(!q.trim()){ setResults([]); setShowResults(false); return; }
    const lower=q.toLowerCase();
    const found=SIGHTINGS.filter(s=>
      s.name.toLowerCase().includes(lower)||
      s.region.toLowerCase().includes(lower)||
      s.classification.toLowerCase().includes(lower)||
      s.description.toLowerCase().includes(lower)||
      s.date.includes(lower)
    );
    setResults(found.slice(0,8));
    setShowResults(true);
  },[isPro]);

  const handleQueryChange=(v)=>{ setQuery(v); doSearch(v); };

  // ── Select incident ───────────────────────────────────────────────────────
  const handleSelect = useCallback(async(inc)=>{
    if(!inc) return;
    setSelected(inc);
    setShowResults(false);
    setShowLeft(false);
    setShowRight(true);

    // INSTANT SCORES — computed from factual data immediately, no waiting
    const instant = computeInstantScores(inc);
    setAiPred({
      ...instant,
      globalThreatLevel: globalThreat,
      activeAlerts:      liveThreats.slice(0,3),
      engineCycle:       0,
      allPredictions:    livePredictions,
      // Timing placeholder
      nextLocation:      livePredictions[0]?.nextLocation,
      timing:            livePredictions[0]?.timing,
      trackingId:        inc.tracking,
      basisFactors: [
        instant.detectionType,
        instant.militaryCorrelation,
        `${instant.physicsAnomalyScore}/100 physics anomaly`,
        `${inc.witnesses?.toLocaleString()||'Unknown'} witnesses`,
        `${(inc.source||'').split('·').length} corroborating sources`,
      ].filter(Boolean),
      isInstant: true, // Flag to show "AI enriching..." state
    });
    setAiLoading(false); // Don't block — show instant scores immediately

    // Gentle camera pan
    if(globeRef.current && inc.lat && inc.lng) {
      globeRef.current.zoomTo(inc.lat, inc.lng, 5);
    }

    // AI enrichment runs in background — upgrades scores when ready
    setAiLoading(true);
    try{
      const r = await aiPredict({ sighting: inc });
      if(r?.prediction) {
        setAiPred(prev => ({
          ...prev,
          ...r.prediction,
          // Preserve instant scores as fallback if AI returns 0
          physicsAnomalyScore: r.prediction.physicsAnomalyScore || prev.physicsAnomalyScore,
          incidentVeracity:    r.prediction.incidentVeracity    || prev.incidentVeracity,
          geographicRiskFactor:r.prediction.geographicRiskFactor|| prev.geographicRiskFactor,
          globalThreatLevel:   globalThreat,
          activeAlerts:        liveThreats.slice(0,3),
          allPredictions:      livePredictions,
          isInstant:           false,
        }));
      }
    } catch(_) {
      // Keep instant scores — they're already showing
      setAiPred(prev => prev ? { ...prev, isInstant:false } : prev);
    }
    finally{ setAiLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[globalThreat, liveThreats, livePredictions]);

  const handleShowPredictionPath = useCallback(()=>{
    // Use live engine predictions if available, with full metadata
    const preds = livePredictions.length > 0
      ? livePredictions.map(p => ({
          lat:        p.nextLocation?.lat,
          lng:        p.nextLocation?.lng,
          region:     p.nextLocation?.name || p.nextLocation?.region,
          confidence: p.confidence,
          tracking:   p.trackingId,
          date:       p.timing?.displayDate,
          time:       p.timing?.displayTime,
        })).filter(p => p.lat && p.lng)
      : PREDICTIONS.map(p => ({
          lat:        p.lat,
          lng:        p.lng,
          region:     p.region,
          confidence: p.confidence,
          tracking:   p.tracking,
          date:       p.date,
        }));

    if (preds.length < 2) {
      showToast('Not enough prediction points yet — AI is computing...', 'info');
      return;
    }

    if(globeRef.current){
      globeRef.current.showPredictionPath(preds);
      showToast(`📡 ${preds.length} AI prediction nodes plotted`, 'success');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[livePredictions]);

  const handleZoomLevel = useCallback(()=>{},[]);
  const alertColor=l=>l==='RED'?'rgba(255,69,69,0.88)':l==='AMBER'?'rgba(255,170,0,0.88)':'rgba(29,224,80,0.88)';

  const toggleLayer = useCallback((id) => {
    setActiveLayers(prev => prev.includes(id) ? prev.filter(l=>l!==id) : [...prev,id]);
  },[]);

  return (
    <div style={{position:'fixed',inset:0,background:C.bg,overflow:'hidden',fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',color:C.text}}>

      {/* Globe — real seismic, atmospheric, and UAP events */}
      <GlobeCanvas
        ref={globeRef}
        sightings={[
          ...SIGHTINGS,
          ...seismicEvents.map(s=>({...s, isSeismic:true})),
          ...atmosphericEvts.map(a=>({...a, isAtmospheric:true})),
        ]}
        predictions={livePredictions.length > 0
          ? livePredictions.map(p=>({
              id:         p.id,
              lat:        p.nextLocation?.lat,
              lng:        p.nextLocation?.lng,
              region:     p.nextLocation?.region,
              confidence: p.confidence,
              tracking:   p.trackingId,
              date:       p.timing?.displayDate,
              time:       p.timing?.displayTime,
            })).filter(p=>p.lat)
          : PREDICTIONS
        }
        seismicEvents={seismicEvents}
        selectedId={selected?.id}
        onSelect={handleSelect}
        onZoomLevel={handleZoomLevel}
      />

      {/* TOP BAR */}
      <div style={{position:'absolute',top:0,left:0,right:0,zIndex:20,paddingTop:'max(12px,env(safe-area-inset-top))',padding:'max(12px,env(safe-area-inset-top)) 12px 10px',background:'linear-gradient(to bottom,rgba(5,8,11,0.94) 0%,rgba(5,8,11,0) 100%)',pointerEvents:'none'}}>
        <div style={{display:'flex',alignItems:'center',gap:9,height:46,pointerEvents:'all'}}>
          {/* Logo */}
          <div style={{display:'flex',alignItems:'center',gap:7,flexShrink:0}}>
            <div style={{width:30,height:30,borderRadius:'50%',background:'rgba(8,14,10,0.92)',border:`1.5px solid ${C.borderGreen}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <AlienSVG size={16} color={C.green}/>
            </div>
            <span style={{fontWeight:800,fontSize:16,letterSpacing:2.5,color:'#e4ede7'}}>A.D.M</span>
          </div>

          {/* Search */}
          <div style={{flex:1,position:'relative'}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(115,148,125,0.5)" strokeWidth="2.5" style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}>
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input value={query} onChange={e=>handleQueryChange(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'&&results.length>0)handleSelect(results[0]);if(e.key==='Escape'){setShowResults(false);setQuery('');}}}
              placeholder="Search incidents, locations..."
              style={{width:'100%',paddingLeft:32,paddingRight:query?30:12,height:40,background:'rgba(9,13,18,0.90)',border:`1px solid ${C.border}`,borderRadius:22,color:C.text,fontSize:13,fontFamily:'inherit',outline:'none',backdropFilter:'blur(14px)',boxSizing:'border-box'}}
            />
            {query&&<span onClick={()=>{setQuery('');setResults([]);setShowResults(false);}} style={{position:'absolute',right:11,top:'50%',transform:'translateY(-50%)',cursor:'pointer',color:C.dimmer,fontSize:17,lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</span>}

            {/* Search dropdown */}
            {showResults&&results.length>0&&(
              <div style={{position:'absolute',top:'calc(100% + 6px)',left:0,right:0,background:'rgba(7,11,16,0.98)',border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden',boxShadow:'0 16px 48px rgba(0,0,0,0.6)',backdropFilter:'blur(20px)',zIndex:50}}>
                {results.map(r=>(
                  <div key={r.id} onClick={()=>handleSelect(r)} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:`1px solid ${C.border}`,cursor:'pointer',WebkitTapHighlightColor:'transparent'}}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(29,224,80,0.06)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <AlienSVG size={17} color={C.green}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.name}</div>
                      <div style={{fontSize:11,color:C.dim}}>{r.region} · {r.date}</div>
                    </div>
                    <div style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:`${tc(r.threatLevel)}18`,border:`1px solid ${tc(r.threatLevel)}40`,color:tc(r.threatLevel),fontWeight:700,flexShrink:0}}>{r.threatLevel}</div>
                  </div>
                ))}
                <div style={{padding:'7px 14px',fontSize:10,color:C.dimmer}}>{results.length} result{results.length!==1?'s':''} · tap to zoom</div>
              </div>
            )}
            {showResults&&results.length===0&&query.length>1&&(
              <div style={{position:'absolute',top:'calc(100% + 6px)',left:0,right:0,background:'rgba(7,11,16,0.98)',border:`1px solid ${C.border}`,borderRadius:10,padding:'14px',textAlign:'center',fontSize:12,color:C.dimmer,backdropFilter:'blur(20px)'}}>
                No incidents found for "{query}"
              </div>
            )}
          </div>

          {/* Sightings icon */}
          <button onClick={()=>setShowLeft(true)} style={{background:'rgba(9,13,18,0.90)',border:`1px solid ${C.border}`,borderRadius:11,width:40,height:40,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',backdropFilter:'blur(14px)',flexShrink:0,WebkitTapHighlightColor:'transparent'}}>
            <AlienSVG size={16} color={C.dim}/>
          </button>

          {/* AI Intel icon */}
          <button onClick={()=>setShowRight(true)} style={{position:'relative',background:showRight?C.greenDim:'rgba(9,13,18,0.90)',border:`1px solid ${showRight?C.borderGreen:C.border}`,borderRadius:11,width:40,height:40,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',backdropFilter:'blur(14px)',flexShrink:0,color:showRight?C.green:C.dim,WebkitTapHighlightColor:'transparent'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            {alertCount>0&&<div style={{position:'absolute',top:6,right:6,width:7,height:7,borderRadius:'50%',background:C.red,border:`1.5px solid ${C.bg}`}}/>}
          </button>

          {/* Layers button */}
          <button onClick={()=>setShowLayers(l=>!l)} style={{background:showLayers?'rgba(29,224,80,0.12)':'rgba(9,13,18,0.90)',border:`1px solid ${showLayers?C.borderGreen:C.border}`,borderRadius:11,width:40,height:40,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',backdropFilter:'blur(14px)',flexShrink:0,color:showLayers?C.green:C.dim,WebkitTapHighlightColor:'transparent'}} title="Intelligence Layers">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="4" rx="1"/><rect x="2" y="10" width="20" height="4" rx="1"/><rect x="2" y="17" width="20" height="4" rx="1"/></svg>
          </button>

          {/* User account icon */}
          <button
            onClick={()=>{ if(user){ if(isPro){showToast(`✅ Pro · ${user.email}`,'success');}else{openPro('alert');} }else{setAuthMode('signin');setShowAuth(true);} }}
            title={user?`${user.email} — ${isPro?'Pro':'Free'}`:'Sign in'}
            style={{background:user?(isPro?'rgba(255,170,0,0.12)':C.greenDim):'rgba(9,13,18,0.90)',border:`1px solid ${user?(isPro?'rgba(255,170,0,0.3)':C.borderGreen):C.border}`,borderRadius:11,width:40,height:40,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',backdropFilter:'blur(14px)',flexShrink:0,WebkitTapHighlightColor:'transparent'}}
          >
            {user
              ? <div style={{width:22,height:22,borderRadius:'50%',background:isPro?C.amber:C.green,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'#07090c'}}>
                  {(user.displayName||user.email||'U')[0].toUpperCase()}
                </div>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.dim} strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            }
          </button>
        </div>
      </div>

      {/* LIVE ALERT TICKER — WebSocket alerts + DEEPSPACE threat alerts */}
      <div style={{position:'absolute',top:'max(68px,calc(env(safe-area-inset-top)+58px))',left:0,right:0,zIndex:15,height:26,overflow:'hidden',background:'linear-gradient(to right,rgba(5,8,11,0.72),rgba(5,8,11,0.42),rgba(5,8,11,0.72))',display:'flex',alignItems:'center',pointerEvents:'none'}}>
        <div style={{display:'flex',gap:36,whiteSpace:'nowrap',paddingLeft:14,animation:'adm-ticker 55s linear infinite',fontSize:10,fontVariantNumeric:'tabular-nums'}}>
          {/* DEEPSPACE engine status prefix */}
          <span style={{color:C.green,fontWeight:700}}>
            ⚡ DEEPSPACE v4.0 ONLINE
          </span>
          <span style={{color:alertColor(globalThreat),fontWeight:700}}>
            GLOBAL THREAT: {globalThreat}
          </span>
          {/* Real USGS seismic data */}
          {seismicEvents.filter(s=>(s.magnitude||0)>=4.5).slice(0,3).map((q,i)=>(
            <span key={`q${i}`} style={{color:alertColor(q.threatLevel||'AMBER')}}>
              🌍 M{q.magnitude?.toFixed(1)} — {q.region?.split(",")[0]} (USGS LIVE)
            </span>
          ))}
          {/* Real NOAA solar data */}
          {realSolarData && (
            <span style={{color:realSolarData.geomagneticStorm?C.red:C.green}}>
              ☀ NOAA Kp={realSolarData.kpIndex} {realSolarData.geomagneticStorm?'— GEOMAGNETIC STORM':''} · {realSolarData.lunarPhase}
            </span>
          )}
          {liveThreats.slice(0,3).map((t,i)=>(
            <span key={`t${i}`} style={{color:alertColor(t.level||'AMBER')}}>
              ☢ {t.name} — Risk {t.risk}%
            </span>
          ))}
          {/* WebSocket live alerts */}
          {[...alerts,...alerts].map((a,i)=>(
            <span key={i} style={{color:alertColor(a.level)}}>
              <span style={{color:C.dimmer,marginRight:7}}>{a.time}</span>{a.msg}
            </span>
          ))}
        </div>
      </div>

      {/* BOTTOM TOOLBAR */}
      <div style={{position:'absolute',bottom:0,left:0,right:0,zIndex:20,paddingBottom:'max(14px,env(safe-area-inset-bottom))',padding:'8px 12px max(14px,env(safe-area-inset-bottom))',background:'linear-gradient(to top,rgba(5,8,11,0.96) 0%,rgba(5,8,11,0) 100%)'}}>
        <div style={{display:'flex',gap:14,justifyContent:'center',marginBottom:10,flexWrap:'wrap'}}>
          {[
            {icon:<span style={{fontSize:10}}>◈</span>,            label:'UAP Signal',  color:C.green},
            {icon:<span style={{fontSize:9,color:C.red}}>◎</span>, label:'Active',      color:C.red},
            {icon:<span style={{fontSize:9,color:C.amber}}>◎</span>,label:'Seismic',     color:C.amber},
            {icon:<span style={{fontSize:9,color:'#00cfff'}}>⟳</span>,label:'Atmospheric', color:'#00cfff'},
            {icon:<svg width="16" height="3"><line x1="0" y1="1.5" x2="16" y2="1.5" stroke={C.green} strokeWidth="1.5" strokeDasharray="4,3"/></svg>,label:'Pred. Path'},
          ].map(item=>(
            <div key={item.label} style={{display:'flex',alignItems:'center',gap:4}}>
              <div style={{display:'flex',alignItems:'center',color:item.color||C.dim}}>{item.icon}</div>
              <span style={{fontSize:9,color:C.dim}}>{item.label}</span>
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'center'}}>
          <button onClick={()=>setShowLeft(true)} style={{background:'rgba(9,13,18,0.92)',border:`1px solid ${C.border}`,borderRadius:24,padding:'10px 18px',color:C.dim,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',backdropFilter:'blur(14px)',display:'flex',alignItems:'center',gap:6,WebkitTapHighlightColor:'transparent',position:'relative'}}>
            <AlienSVG size={13} color={C.dim}/>Events
            {(seismicEvents.length+atmosphericEvts.length)>0&&(
              <span style={{fontSize:9,padding:'1px 5px',borderRadius:8,background:'rgba(255,170,0,0.15)',color:C.amber,fontWeight:700,border:'1px solid rgba(255,170,0,0.3)'}}>
                {seismicEvents.length+atmosphericEvts.length} LIVE
              </span>
            )}
          </button>
          <button onClick={()=>setShowRight(true)} style={{background:C.greenDim,border:`1px solid ${C.borderGreen}`,borderRadius:24,padding:'10px 18px',color:C.green,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',backdropFilter:'blur(14px)',display:'flex',alignItems:'center',gap:6,WebkitTapHighlightColor:'transparent'}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/></svg>AI Intel
          </button>
          {isPro
            ? <button style={{background:'rgba(255,170,0,0.1)',border:'1px solid rgba(255,170,0,0.28)',borderRadius:24,padding:'10px 18px',color:C.amber,fontSize:12,fontWeight:700,cursor:'default',fontFamily:'inherit',backdropFilter:'blur(14px)'}}>✅ Pro</button>
            : isPro
              ? <button onClick={()=>showToast(`✅ Pro Active · ${user?.email}`,'success')} style={{background:'rgba(29,224,80,0.1)',border:'1px solid rgba(29,224,80,0.3)',borderRadius:24,padding:'10px 18px',color:'#1de050',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',backdropFilter:'blur(14px)',WebkitTapHighlightColor:'transparent',display:'flex',alignItems:'center',gap:5}}>✅ Pro</button>
              : <button onClick={()=>openPro('default')} style={{background:'rgba(255,170,0,0.1)',border:'1px solid rgba(255,170,0,0.28)',borderRadius:24,padding:'10px 18px',color:C.amber,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',backdropFilter:'blur(14px)',WebkitTapHighlightColor:'transparent'}}>⚡ Pro</button>
          }
        </div>
      </div>

      {/* SELECTED INCIDENT CARD */}
      {selected&&!showLeft&&!showRight&&!showStreet&&(
        <div style={{position:'absolute',bottom:'max(110px,calc(env(safe-area-inset-bottom)+100px))',left:12,right:12,zIndex:25,background:'rgba(7,11,16,0.97)',border:`1px solid ${C.border}`,borderRadius:14,padding:'14px 15px',backdropFilter:'blur(22px)',animation:'adm-up 0.2s ease-out',boxShadow:'0 12px 40px rgba(0,0,0,0.55)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:7}}>
            <div style={{flex:1,marginRight:10}}>
              <div style={{fontSize:15,fontWeight:700,color:C.text,lineHeight:1.2}}>{selected.name}</div>
              <div style={{fontSize:11,color:C.dim,marginTop:3}}>{selected.region}</div>
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
              <div style={{fontSize:10,padding:'3px 8px',borderRadius:5,background:`${tc(selected.threatLevel)}18`,border:`1px solid ${tc(selected.threatLevel)}40`,color:tc(selected.threatLevel),fontWeight:700}}>{selected.threatLevel}</div>
              <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:C.dimmer,fontSize:19,cursor:'pointer',lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</button>
            </div>
          </div>
          <div style={{fontSize:12,color:C.dim,lineHeight:1.65,marginBottom:8}}>{selected.description.slice(0,150)}...</div>
          <div style={{fontSize:10,color:C.dimmer,marginBottom:10,fontStyle:'italic'}}>Source: {selected.source}</div>
          <div style={{display:'flex',gap:7}}>
            <Btn onClick={()=>setShowRight(true)} style={{fontSize:12,padding:'9px'}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/></svg>AI Analysis
            </Btn>
            <Btn onClick={()=>{if(globeRef.current&&selected.lat&&selected.lng) globeRef.current.zoomTo(selected.lat,selected.lng,8);}} variant="ghost" style={{fontSize:12,padding:'9px'}}>
              📍 Locate
            </Btn>
            <Btn onClick={()=>{setStreetTarget({lat:selected.lat,lng:selected.lng,name:selected.name,region:selected.region});setShowStreet(true);}} variant="ghost" style={{fontSize:12,padding:'9px'}}>
              🛰 Street
            </Btn>
            {selected.sourceUrl&&(
              <a href={selected.sourceUrl} target="_blank" rel="noopener noreferrer" style={{flex:1}}>
                <Btn variant="ghost" style={{fontSize:12,padding:'9px'}}>Source ↗</Btn>
              </a>
            )}
          </div>
        </div>
      )}

      {/* PRO BANNER for free logged-in users */}
      {showBanner && !isPro && !showPro && !showOnboarding && (
        <SupportBar
          onDonate={()=>{ setShowBanner(false); setShowDonation(true); }}
          onUpgrade={()=>{ setShowBanner(false); openPro('alert'); }}
          onDismiss={()=>{
            setShowBanner(false);
            bannerDismissed.current = true;
            localStorage.setItem('adm_banner_dismissed','true');
            localStorage.setItem('adm_support_bar_last', String(Date.now()));
          }}
        />
      )}

      {/* LEFT SHEET — Sightings */}
      {showLeft&&(
        <>
          <div onClick={()=>setShowLeft(false)} style={{position:'fixed',inset:0,zIndex:90,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(5px)'}}/>
          <div style={{position:'fixed',top:0,left:0,bottom:0,zIndex:100,width:'min(330px,90vw)',background:C.surface,backdropFilter:'blur(28px)',borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',paddingTop:'max(52px,env(safe-area-inset-top))',paddingBottom:'max(16px,env(safe-area-inset-bottom))',animation:'adm-slideRight 0.22s ease-out',boxShadow:'10px 0 50px rgba(0,0,0,0.55)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0 18px 12px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <span style={{fontSize:11,fontWeight:700,letterSpacing:2,color:C.dim,textTransform:'uppercase'}}>Sightings Database</span>
              <button onClick={()=>setShowLeft(false)} style={{background:'none',border:'none',color:C.dimmer,fontSize:20,cursor:'pointer',lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</button>
            </div>
          {/* Sightings LEFT SHEET with live data tabs */}
          <div style={{padding:'8px 14px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <input value={query} onChange={e=>handleQueryChange(e.target.value)} placeholder="Filter all events..."
              style={{width:'100%',padding:'8px 12px',background:'rgba(10,16,14,0.8)',border:'1px solid rgba(32,68,42,0.4)',borderRadius:7,color:C.text,fontSize:12,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
          </div>
          {/* Category tabs */}
          <SightingsTabPanel
            sightings={SIGHTINGS}
            seismicEvents={seismicEvents}
            atmosphericEvents={atmosphericEvts}
            selected={selected}
            query={query}
            onSelect={handleSelect}
          />
          </div>
        </>
      )}

      {/* RIGHT SHEET — AI Intel */}
      {showRight&&(
        <>
          <div onClick={()=>setShowRight(false)} style={{position:'fixed',inset:0,zIndex:90,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(5px)'}}/>
          <div style={{position:'fixed',top:0,right:0,bottom:0,zIndex:100,width:'min(310px,92vw)',background:C.surface,backdropFilter:'blur(28px)',borderLeft:`1px solid ${C.border}`,display:'flex',flexDirection:'column',paddingTop:'max(52px,env(safe-area-inset-top))',paddingBottom:'max(16px,env(safe-area-inset-bottom))',animation:'adm-slideLeft 0.22s ease-out',boxShadow:'-10px 0 50px rgba(0,0,0,0.55)',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0 18px 12px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <span style={{fontSize:11,fontWeight:700,letterSpacing:2,color:C.dim,textTransform:'uppercase'}}>AI Intelligence</span>
              <button onClick={()=>setShowRight(false)} style={{background:'none',border:'none',color:C.dimmer,fontSize:20,cursor:'pointer',lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</button>
            </div>
            <AIPanelContent aiPred={aiPred} aiLoading={aiLoading} selected={selected} isPro={isPro} onProClick={openPro} onShowPredictionPath={handleShowPredictionPath}/>
          </div>
        </>
      )}

      {/* STREET VIEW */}
      {showStreet&&streetTarget&&(
        <StreetView lat={streetTarget.lat} lng={streetTarget.lng} name={streetTarget.name} region={streetTarget.region}
          onBack={()=>{setShowStreet(false);if(globeRef.current)globeRef.current.zoomTo(streetTarget.lat,streetTarget.lng,8);}}
          onClose={()=>{setShowStreet(false);if(globeRef.current)globeRef.current.resetView();}}
        />
      )}

      {/* ONBOARDING — new visitors */}
      {showOnboarding&&(
        <OnboardingScreen
          onSignUp={()=>{localStorage.setItem('adm_onboarded','1');setShowOnboarding(false);setAuthMode('signup');setShowAuth(true);setTimeout(()=>setShowPro(true),800);}}
          onSignIn={()=>{localStorage.setItem('adm_onboarded','1');setShowOnboarding(false);setAuthMode('signin');setShowAuth(true);}}
          onSkip={()=>{localStorage.setItem('adm_onboarded','1');setShowOnboarding(false);}}
        />
      )}

      {/* AI CONSCIOUSNESS STREAM — real thoughts from cognition engine */}
      {!showStreet && !authLoading && (
        <AIConsciousnessStream
          globalThreat={globalThreat}
          seismicCount={seismicEvents.length}
          solarKp={realSolarData?.kpIndex}
          majorQuakes={seismicEvents.filter(s=>(s.magnitude||0)>=5.0).length}
          aiThoughts={aiThoughts}
          aiFocus={aiFocus}
          aiDiscoveries={aiDiscoveries}
        />
      )}

      {/* INTELLIGENCE LAYERS PANEL */}
      {showLayers && !showStreet && (
        <LayerPanel
          activeLayers={activeLayers}
          onToggle={toggleLayer}
          onClose={()=>setShowLayers(false)}
        />
      )}

      {/* AUTH MODAL */}
      {showAuth&&(
        <AuthModal initialMode={authMode} onClose={()=>setShowAuth(false)}
          onSuccess={()=>{setShowAuth(false);showToast('✓ Welcome to A.D.M','success');}}
        />
      )}

      {/* DONATION MODAL */}
      {showDonation&&!isPro&&(
        <DonationModal onClose={()=>setShowDonation(false)}
          onSuccess={()=>{refreshProStatus();setShowDonation(false);showToast('✓ Thank you!','success');}}
          onShowPro={()=>{setShowDonation(false);openPro('default');}}
        />
      )}

      {/* PRO UPGRADE MODAL */}
      {showPro&&(
        <ProModal trigger={proTrigger} onClose={()=>setShowPro(false)}
          onSuccess={()=>{refreshProStatus();setShowPro(false);showToast('🛸 Pro activated!','success');}}
        />
      )}

      {/* AUTH LOADING */}
      {authLoading&&(
        <div style={{position:'fixed',inset:0,zIndex:300,background:'rgba(7,10,14,0.96)',display:'flex',alignItems:'center',justifyContent:'center',gap:14,flexDirection:'column'}}>
          <div style={{width:36,height:36,border:'3px solid rgba(29,224,80,0.2)',borderTop:'3px solid #1de050',borderRadius:'50%',animation:'adm-spin 0.8s linear infinite'}}/>
          <div style={{fontSize:13,color:'rgba(140,175,150,0.6)'}}>Connecting to A.D.M...</div>
          <div style={{fontSize:10,color:'rgba(100,130,115,0.4)',marginTop:-8}}>Tap anywhere to continue</div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/"        element={<GlobeApp/>}/>
      <Route path="/success" element={<SuccessPage/>}/>
    </Routes>
  );
}
